// The game client: wires netcode, input, camera, the views, the HUD and sound.
// Owns no rules; everything gameplay comes from sim/ (predicted) or the server.

import * as THREE from 'three';
import { TICK_RATE, aimDir, clamp, raySphere, smoothstep, wrapAngle } from '../sim/core';
import { Ev, GameEvent } from '../sim/codec';
import { BKind, BULLETS, bulletPosAt } from '../sim/bullets';
import { CARDS, HAND_FIELDS } from '../sim/cards';
import { CardDraft, openCardDraft } from './ui/cards';
import { CLASSES, ClassId, VDASH_CHARGES, VDASH_RECHARGE, ENEMIES, EType, EYE_HEIGHT, HULL_MAX, PLAYER_HEIGHT, PLAYER_RADIUS, isBossType } from '../sim/defs';
import { DirState, ShotFlag, shotRay, shotTracks, trackRay, dropSegments, ShotSeg } from '../sim/game';
import { Btn, InputFrame, Move, Phase, PlayerState, StepOut, activeWeaponId, barrelSide, renderY, weaponOf } from '../sim/player';
import { EFlag, MFlag, NetEnemy, PFlag, RemotePlayer } from '../sim/protocol';
import { ISLANDS_PER_VILLAGE, IslandKind, World, RayHit, NO_GROUND, WATER } from '../sim/world';
import { BossKind } from '../sim/bosses/types';
import { PURSUER } from '../sim/bosses/index';
import { BONUS_CLOCK_SECS, BONUS_WAVES, HOLDOUT_SECS, RunPhase, WARP_SECS, islandName } from '../sim/run';
import type { RunSnap } from '../sim/runwire';
import { NetSession } from './net/session';
import { Input, Settings } from './input/input';
import { CameraRig } from './camera';
import { createStage, Stage } from './render/stage';
import { HAZE } from './render/materials';
import type { CowboyRS, EnemyRS, HudClassCue, HudMarker, HudSight, HudState, HudTeammate, ViewCtx } from './render/contracts';
import { WorldView } from './render/world/index';
import { IslandPrefetch, nextIslands } from './render/world/prefetch';
import { LightBudget, budget, warmGroup } from './render/warm';
import { EnemyView } from './render/robots/index';
import { CowboyView } from './render/cowboys/index';
import { El, Fx } from './render/fx/index';
import { ShieldTracker, shieldBlock, shieldShatter } from './render/fx/shieldskin';
import { ClassFx } from './render/fx/classfx';
import { SFX, Sfx, type MusicState } from './audio/sfx';
import { WorldAudio } from './audio/world_audio';
import { Hud } from './hud/hud';
import { CritterView } from './render/run/critters';
import { BOSS_CLIENTS } from './render/run/bosses/index';
import { BossAim, BossClient, BossCtx, BossFrame, newBossAim, newHudBoss } from './render/run/bosses/types';
import { ExtractView } from './render/run/extract';
import { PowerUpView } from './render/run/powerups';
import { CHUG_TICKS, HEAL_TICKS, WHISKEY_MAX } from '../sim/whiskey';
import { PU, PUEv, PU_DEFS, puColor, puLabel } from '../sim/powerups';
import { WeaponsClient, weaponSfx, weaponStyle } from './weapons/client';
import { ASSIST, assistY, bendShot, stanceMul } from './weapons/aimassist';
import { ViewModel } from './weapons/viewmodel';
import { FAN_SPREAD, Loadout, Perk, Proj, SPIN_TICKS, STEADY_TICKS, ShotMod, WEAPONS, WeaponDef, weaponDef, W } from '../sim/weapons';
import { Feel, raisedMuzzle } from './feel/director';
import type { LookWire, Tracker } from './progress/ledger';
import { ReviewLayer } from './review/index';
const PU_SECS = PU_DEFS.map((d) => d.secs);
import { SaloonDirector } from './saloon/director';
import { RunStage, SaloonReason } from '../sim/saloon';
import { loadStoredLoadout, saveLoadout } from './ui/loadout';
import { CARD_RANGE, PICKUP_BIND, PickupCard } from './hud/pickup';

export interface RosterEntry {
  id: number; name: string; slot: number; cls: number; on: boolean;
  /** Service Record look (sim/progression.ts): gun finish, earned-contract mask, worn title */
  fin?: number; k?: number; ttl?: string;
}

const ENEMY_NAMES = ['SWARMER', 'SHOOTER', 'BRUTE', 'SNIPER', 'BOMBER', 'METAL BRUTE'];

// read at load: the boot rewrites the URL to ?room=… before the game is made
const HITCH_PROBE = new URLSearchParams(location.search).has('hitch');

/** each class's chain-window loop extender, as the HUD names it (GAMEPLAY_LOOP §1.2) */
const EXTENDER = ['HOT SHOT · ANY HEADSHOT DETONATES', 'SKY-STALKER · STAY UP, PRIMES LAST LONGER', 'SMOKE & MIRRORS · CLOAKED', 'SHIELD CHARGE · BURNT SHIELD REFILLS YOUR KIT'];

export class GameClient {
  stage: Stage;
  rig: CameraRig;
  input: Input;
  session: NetSession;
  world!: World;
  sfx: Sfx;
  hud: Hud;
  private worldView: WorldView | null = null;
  /** the islands a warp can land on next, built in the background (render/world/prefetch.ts) */
  private prefetch: IslandPrefetch;
  /** the point-light count stays fixed, so lights coming and going never recompile the scene */
  private lights = new LightBudget(4);
  /** when the island on screen was built (the next one starts building once the warp-in has settled) */
  private worldAt = 0;
  /** the island hidden behind the saloon has been warmed for the blast-off */
  private warmedView: WorldView | null = null;
  private warmedExtract: ExtractView | null = null;
  private robots: EnemyView | null = null;
  private cowboys: CowboyView | null = null;
  /** first-person ADS: the gun and gauntlets in front of the eye (weapons/viewmodel.ts) */
  private viewmodel: ViewModel | null = null;
  private fx: Fx | null = null;
  private classFx: ClassFx | null = null;
  private critterView: CritterView | null = null;
  /** boss views by kind, built when an island first announces its boss (client/render/run/bosses) */
  private bossViews = new Map<BossKind, BossClient>();
  /** the view of this island's boss (kept after it dies so its death plays out) */
  private bossView: BossClient | null = null;
  private bossCtx: BossCtx | null = null;
  private extractView: ExtractView | null = null;
  private powerupView: PowerUpView | null = null;
  private nCow = 0;
  private puDeniedAt = -9;
  /** the pickup card: what the crate or crystal you're next to is (client/hud/pickup.ts) */
  private pickupCard = new PickupCard();
  // the run (sim/run.ts via the snapshot's run block)
  /** this frame's boss bodies, split out of the robot list, and what the boss view is handed */
  private bossFrame: BossFrame = { net: { kind: BossKind.None, eid: 0, atk: 0, phase: 0 }, units: [], n: 0, primary: null, hitAge: (id) => { const h = this.enemyHitAt.get(id); return h === undefined ? 99 : this.time - h; }, dt: 0, time: 0, ground: (x, z) => { const g = this.world.terrain(x, z); return g === NO_GROUND ? 0 : g; } };
  private bossLive = false;
  private bossAim: BossAim = newBossAim();
  private aimBossPart = '';
  private extractT = 0;
  private endedAt = -1;
  private runResult = -1;
  private lastPhase = -1;
  /** guns on the cowboys, grenades, ordnance, crates (client/weapons) */
  weapons: WeaponsClient | null = null;
  /** shooting feel: lens punch, hit-stop, markers, robot reactions, deaths, rumble, triggers (client/feel) */
  feel: Feel | null = null;
  private fxTime = 0;
  private muzzleAt = new Map<number, number>();
  private shotFrom = new THREE.Vector3();
  private shotDir = new THREE.Vector3();
  /** the saloon car, Pop, the launch and the hotdog scene (client/saloon) */
  saloon: SaloonDirector | null = null;
  roster = new Map<number, RosterEntry>();
  private time = 0;
  private gateCh = 0;
  private last = performance.now();
  private alpha = 0;
  // aim, resolved from the camera each frame and sent with the next tick
  private aimYaw = 0;
  private aimPitch = 0;
  private aimTarget: NetEnemy | null = null;
  private aimDist = 0;
  private aimPt = { x: 0, y: 0, z: 0, eid: 0 };
  private hit: RayHit = { t: 0, kind: 0, nx: 0, ny: 0, nz: 0 };
  // pools
  private netEnemies: NetEnemy[] = [];
  private enemyRS: EnemyRS[] = [];
  private netPlayers: RemotePlayer[] = [];
  private netPlayerCount = 0;
  private cowboyRS: CowboyRS[] = [];
  private bulletList: { id: number; kind: number; x: number; y: number; z: number; vx: number; vy: number; vz: number; age: number }[] = [];
  private enemyCount = 0;
  private prevEnemyPos = new Map<number, { x: number; z: number; spd: number }>();
  private enemyHitAt = new Map<number, number>();
  private lastShotAt = new Map<number, number>();
  private meleeAt = new Map<number, number>();
  /** heal lane: when each cowboy uncorked his last bottle (client clock), for the chug, the toss and the sounds */
  private drinkAt = new Map<number, number>();
  /** how each cowboy ditches his next empty (rolled by the server, sim/whiskey.ts Ditch) */
  private ditch = new Map<number, number>();
  /** the blade flourish each cowboy's last swing played (Ev.Melee), so remote combos animate in step */
  private meleeStep = new Map<number, number>();
  // hud timers
  private hitAt = -9; private killAt = -9; private headAt = -9; private detAt = -9; private deniedAt = -9; private shieldBrokeAt = -9;
  private hurtDirs: { angle: number; at: number; wx: number; wz: number }[] = [];
  private notices: { text: string; at: number }[] = [];
  private spawnMarks: { x: number; y: number; z: number; until: number }[] = [];
  private bombMarks = new Map<number, { x: number; y: number; z: number; until: number }>();
  private hurtPulse = 0; private flash = 0; private chroma = 0;
  showNet = false;
  showPosse = false;
  private frameMs = 0; private fps = 60; private fpsAcc = 0; private fpsN = 0; private fpsT = 0;
  private v1 = new THREE.Vector3(); private v2 = new THREE.Vector3(); private v3 = new THREE.Vector3();
  private hudState: HudState;
  private jetOn = false;
  private worldAudio: WorldAudio;
  private bonusTick = -1;
  private jetOf = (id: number) => this.cowboys?.jetOf?.(id) ?? 0;
  private draws = 0;
  private tris = 0;
  /** design-review layer: callouts, pings, rumble, coach, profile, Paperwork (client/review) */
  review: ReviewLayer;
  onRoster: () => void = () => {};
  onDisconnect: (reason: string) => void = () => {};
  /** a welcome after the first: the session dialled back in */
  onRejoin: () => void = () => {};
  private welcomes = 0;

  constructor(canvas: HTMLCanvasElement, settings: Settings, url: string, name: string, cls: ClassId, token: string, lag: { lag: number; jitter: number; loss: number }) {
    this.stage = createStage(canvas, { quality: settings.quality });
    this.rig = new CameraRig(this.stage.camera);
    this.input = new Input(canvas, settings);
    this.sfx = SFX;
    this.worldAudio = new WorldAudio(this.sfx);
    this.hud = new Hud();
    this.stage.post.setHud(this.hud.texture);
    this.hudState = this.blankHud();
    this.session = new NetSession(url, lag, {
      onPredicted: (p, inp, out, tick) => this.onPredicted(p, inp, out, tick),
      onEvent: (e, tick) => this.onEvent(e, tick),
      onWelcome: (_pid, seed) => this.onWelcome(seed),
      onIsland: () => this.onIsland(),
      onClose: (r) => this.onDisconnect(r),
    }, name, cls, token);
    this.review = new ReviewLayer({
      sfx: this.sfx, input: this.input, session: this.session, roster: this.roster,
      notice: (t) => this.notice(t),
      enemies: () => ({ list: this.netEnemies, n: this.enemyCount }),
      players: () => ({ list: this.netPlayers, n: this.session.players(this.netPlayers) }),
      aim: () => this.aimPt,
      project: (x, y, z, out) => this.project(x, y, z, out),
    });
    this.prefetch = new IslandPrefetch(this.stage.scene, this.stage.camera, this.stage.renderer);
    this.session.worldFor = (seed, index) => this.prefetch.world(seed, index);
    window.addEventListener('resize', () => this.resize());
    this.resize();
    // load-hitch probe (tools/hitchprobe.mjs): swap islands the way a warp does and time the rebuild
    if (HITCH_PROBE) {
      (window as unknown as Record<string, unknown>).__emgWarp = (index: number) => {
        const t = performance.now();
        this.session.pinIsland = true;
        const seed = this.session.world!.runSeed;
        this.session.world = this.prefetch.world(seed, index) ?? new World(seed, index);
        this.onIsland();
        return Math.round(performance.now() - t);
      };
      (window as unknown as Record<string, unknown>).__emgLoad = () => ({ prefetch: this.prefetch.status, busy: budget.busy, spent: Math.round(budget.spent), worstSlice: Math.round(budget.worstSlice), worstAt: budget.worstAt, long: budget.long });
    }
  }

  private resize() {
    this.stage.resize();
    const c = this.stage.renderer.domElement;
    this.hud.resize(c.clientWidth || window.innerWidth, c.clientHeight || window.innerHeight, this.stage.renderer.getPixelRatio());
  }

  /** the saloon's gun rack changed class (main.ts keeps its own copy for the pause menu) */
  onClassPick: ((c: ClassId) => void) | null = null;
  /** the saloon loadout: sent on every welcome (rejoins too) and whenever it changes */
  loadout: Loadout | null = null;
  setLoadout(l: Loadout) {
    this.loadout = l;
    this.session.setLoadout(l.primary, l.secondary, l.grenade);
  }

  /** Service Record (client/progress): counts the run, and your look goes to the posse on every welcome */
  progress: Tracker | null = null;
  look: LookWire | null = null;
  /** the contract the posse signed for its next ride (NO_CONTRACT = none), and where the posse is */
  contract = 255;
  runStage: RunStage = RunStage.Saloon;
  onContract: (() => void) | null = null;
  /** contracts anyone riding has earned (theirs come on the roster, yours from your record) */
  posseContracts() {
    let m = this.look?.contracts ?? 0;
    for (const r of this.roster.values()) if (r.on) m |= r.k ?? 0;
    return m;
  }
  signContract(k: number) { this.session.setContract(k); }
  setLook(w: LookWire) {
    this.look = w;
    this.session.setLook(w.finish, w.contracts, w.title);
  }

  private onWelcome(_seed: number) {
    if (this.welcomes++ > 0) this.onRejoin();
    if (this.loadout) this.session.setLoadout(this.loadout.primary, this.loadout.secondary, this.loadout.grenade);
    if (this.look) this.session.setLook(this.look.finish, this.look.contracts, this.look.title);
    if (this.world && this.world === this.session.world && this.worldView) return;
    this.buildWorld();
  }

  /** The posse warped: the session already swapped to the new island; rebuild what's drawn of it. */
  private onIsland() {
    this.buildWorld();
    // the far side of the doors: a white-gold flash out of the warp
    this.flash = Math.max(this.flash, 1); this.chroma = Math.max(this.chroma, 0.8);
    (this.stage.post.material.uniforms.uFlashColor.value as THREE.Color).setRGB(1, 0.9, 0.7);
    this.spawnMarks.length = 0; this.bombMarks.clear(); this.prevEnemyPos.clear();
    this.gateCh = 0;
  }

  private buildWorld() {
    this.world = this.session.world!;
    const ctx: ViewCtx = { scene: this.stage.scene, camera: this.stage.camera, renderer: this.stage.renderer, world: this.world, quality: this.input.settings.quality };
    // the warp's island was built ahead (its shaders compiled and buffers uploaded): swap it in. Its
    // programs are shared with the old island's materials, so those go only after it's attached
    const pre = this.prefetch.take(this.world);
    this.prefetch.clear(); // the other door's island, or builds for an island the posse never reached
    const old = this.worldView;
    this.worldView = pre ? pre.view : new WorldView(ctx);
    this.worldView.attach();
    if (this.saloon) this.worldView.group.visible = this.saloon.worldShown;
    old?.dispose();
    this.worldAt = performance.now();
    this.worldView.bindPost(this.stage.post);
    if (!this.robots) {
      this.robots = new EnemyView(ctx);
      this.cowboys = new CowboyView(ctx);
      this.fx = new Fx(ctx);
      this.classFx = new ClassFx(this.fx);
      this.weapons = new WeaponsClient(this.stage.scene, this.world, this.fx, this.sfx);
      this.cowboys.setGunHook(this.weapons.rack.place);
      this.viewmodel = new ViewModel(this.stage.scene);
      // the tossed whiskey empty meets the ground: glass, a splash, a stain, the smash (heal lane)
      this.cowboys.ground = (x, z, y) => this.world.support(x, z, y);
      this.cowboys.onBottleShatter = (x, y, z, vx, vy, vz) => {
        this.fx?.glassShatter(x, y, z, vx, vy, vz);
        // a spike lands harder than a lob
        this.sfx.play('glass_shatter', { x, y: y + 0.2, z }, vy < -7 ? { volume: 1.35, pitch: 0.88 } : undefined);
      };
      // your own finish shows at once; everyone else's comes with the roster
      this.weapons.rack.finishOf = (id) => (id === this.session.pid ? this.look?.finish : this.roster.get(id)?.fin) ?? 0;
      this.critterView = new CritterView(ctx.scene);
      this.critterView.prewarm = (g) => budget.add(warmGroup(this.stage.renderer, this.stage.scene, this.stage.camera, g));
      this.bossCtx = {
        scene: ctx.scene, time: HAZE.uTime, sfx: this.sfx, fx: () => this.fx,
        shake: (a) => this.rig.shake(a), rumble: (lo, hi, ms) => this.input.rumble(lo, hi, ms),
        flash: (a) => { this.flash = Math.max(this.flash, a); }, chroma: (a) => { this.chroma = Math.max(this.chroma, a); },
        prewarm: (objs) => { for (const o of objs) budget.add(warmGroup(this.stage.renderer, this.stage.scene, this.stage.camera, o)); },
      };
      this.powerupView = new PowerUpView(ctx.scene, HAZE.uTime);
      this.feel = new Feel(this.rig, this.input, this.sfx, this.fx, this.robots, this.input.settings.quality,
        (id, out) => this.playerPos(id, out), () => this.session.pid);
      this.feel.shakeScale = this.input.settings.shake ?? 1;
    } else { this.fx?.setWorld(this.world); this.weapons?.setWorld(this.world); }
    if (this.fx) this.worldView.bindFx(this.fx);
    this.critterView?.clear();
    this.powerupView?.clear();
    this.extractView?.dispose();
    this.extractView = pre?.extract ?? (this.world.kind === IslandKind.Boss ? new ExtractView(ctx, HAZE.uTime) : null);
    // the saloon car, Pop, the launch and the hotdog scene: built once, they outlive island swaps
    if (!this.saloon) {
      this.saloon = new SaloonDirector({
        stage: this.stage, session: this.session, input: this.input, sfx: this.sfx, fx: () => this.fx,
        shake: (a) => this.rig.shake(a), quality: this.input.settings.quality,
        setWorldVisible: (on) => { if (this.worldView) this.worldView.group.visible = on; },
        // the gun rack: class + loadout picks, saved per class like the pause menu's rack
        kit: {
          cls: () => (this.session.me?.cls ?? 0) as ClassId,
          loadout: (c) => loadStoredLoadout(c),
          setClass: (c) => { this.session.setClass(c); this.setLoadout(loadStoredLoadout(c)); this.onClassPick?.(c); },
          setLoadout: (c, l) => { saveLoadout(c, l); this.setLoadout(l); },
        },
        boneWorld: (id, bone, out) => this.cowboys?.boneWorld(id, bone, out) ?? false,
        myName: () => this.roster.get(this.session.me?.id ?? -1)?.name ?? 'You',
      });
    }
    const sp = this.world.spawnPoint(this.session.slot);
    // face the arena on arrival
    this.input.yaw = Math.atan2(-(this.world.arena.x - sp.x), -(this.world.arena.z - sp.z));
    this.input.pitch = -0.08;
  }

  // ------------------------------------------------------------------ input → tick
  private sample = (tick: number): InputFrame => {
    // aboard the extraction train: held out of the fight like a pause (the server does the same)
    const run = this.session.latest?.run;
    const aboard = !!run && this.runStage === RunStage.Run && ((run.boardMask >> (this.session.pid & 31)) & 1) === 1;
    const paused = this.input.paused || aboard;
    let buttons = this.input.takeButtons();
    // reaching for an empty belt: a dry click and the HUD row flashes (the sim just ignores it)
    const meP = this.session.me;
    if ((buttons & Btn.Drink) && !(this.drinkHeld) && meP && meP.bottles <= 0 && meP.phase === Phase.Alive) { this.dryAt = this.time; this.sfx.play('denied'); }
    this.drinkHeld = (buttons & Btn.Drink) !== 0;
    if (aboard) buttons |= Btn.Pause;
    const f: InputFrame = {
      tick,
      mx: paused ? 0 : this.input.mx,
      my: paused ? 0 : this.input.my,
      // this frame's look plus last frame's parallax offset: the offset drifts slowly, the look doesn't (the full
      // aim from last frame lagged every shot and flight turn by one frame of stick movement)
      yaw: this.input.yaw + this.aimOffYaw,
      pitch: this.input.pitch + this.aimOffPitch,
      myaw: this.input.yaw,
      buttons,
      viewTick: 0,
    };
    this.saloon?.filterInput(f);
    return f;
  };

  /**
   * Background loading, a few ms at the top of every frame: the island hidden behind the saloon is
   * warmed for the blast-off, and the islands a warp can land on are built and warmed while the
   * posse fights, so neither moment builds or compiles anything on the frame it happens.
   */
  private loadSlices() {
    const wv = this.worldView;
    if (wv && this.world && this.session.world === this.world) {
      if (!wv.group.visible && this.warmedView !== wv && wv.loaded) {
        this.warmedView = wv;
        const warm = warmGroup(this.stage.renderer, this.stage.scene, this.stage.camera, wv.group);
        const sun = this.stage.sun;
        budget.add((function* () {
          yield* warm;
          // and one shadow pass aboard (faded out there): the posse's own shadow shaders compile now,
          // not on the first outdoor frame
          sun.shadow.needsUpdate = true;
        })());
      }
      // the boss island's bell and extraction train, hidden until the boss is down
      const ex = this.extractView;
      if (ex && ex !== this.warmedExtract && ex.loaded) {
        this.warmedExtract = ex;
        budget.add(warmGroup(this.stage.renderer, this.stage.scene, this.stage.camera, ex.group));
      }
      if (performance.now() - this.worldAt > 2500) {
        this.prefetch.want(this.world.runSeed, nextIslands(this.world.index), this.input.settings.quality);
        this.prefetch.update();
      }
    }
    // aboard (nothing to fight) the slices can be longer
    budget.run(this.saloon?.aboard ? 6 : 3);
  }

  // ------------------------------------------------------------------ frame
  frame() {
    this.progress?.flush();
    const now = performance.now();
    const dt = Math.min(0.1, (now - this.last) / 1000);
    this.last = now;
    this.time += dt;
    const t0 = performance.now();
    this.loadSlices();
    this.input.poll(dt);
    this.aimAssist(dt);
    if (!this.world) { this.stage.post.render(this.stage.scene, this.stage.camera, this.time); return; }

    this.alpha = this.session.update(dt, this.sample);
    const me = this.session.me;
    const prev = this.session.prev ?? me;

    // ---- own cowboy: interpolate between predicted ticks, plus decaying correction offset
    let px = 0, py = 0, pz = 0;
    this.enemyCount = this.session.enemies(this.netEnemies);
    this.splitBoss();
    if (me && prev) {
      const a = this.alpha;
      px = prev.x + (me.x - prev.x) * a + this.session.smoothX;
      py = renderY(prev) + (renderY(me) - renderY(prev)) * a + this.session.smoothY;
      pz = prev.z + (me.z - prev.z) * a + this.session.smoothZ;
      const speed = Math.hypot(me.vx, me.vy, me.vz);
      this.rig.setEnemies(this.netEnemies, this.enemyCount);
      this.rig.sprint = me.sprint > 0 && me.move === Move.Ground;
      this.input.airborne = me.grounded === 0;
      // a wildcard draft on the table (sim/cards.ts): the overlay takes the pad until a pick or a pass
      if (me.offerKind !== 0 && !this.draft && me.phase !== Phase.Dead) {
        this.input.unlock(); this.input.blocked = true;
        this.draft = openCardDraft(document.body, me, me.offerKind, (slot, wslot) => {
          this.session.pickCard(slot, wslot ?? 255);
          this.draft = null;
          this.input.blocked = false; this.input.swallowPad(); this.input.lock();
        });
      } else if (me.offerKind === 0 && this.draft) { this.draft.close(); this.draft = null; this.input.blocked = false; this.input.swallowPad(); }
      // the left stick flies (CONTROLS.md): the camera chases the line of flight while the right stick is idle
      if (me.move === Move.Flight && speed > 3) { this.input.flightYaw = Math.atan2(-me.vx, -me.vz); this.input.flightPitch = Math.asin(Math.max(-1, Math.min(1, me.vy / speed))); this.input.flying = true; } else this.input.flying = false;
      // caught in a Time grenade: the world drags, the picture shivers and splits (the slowed look)
      if (me.timeT > 0) { this.chroma = Math.max(this.chroma, 0.55); this.rig.shake(0.08); }
      this.rig.distMul = this.saloon?.aboard ? 0.7 : 1; // a 5.6 m wide car wants a short boom
      // first-person ADS: aiming on foot or in hover, alive and free (not rolling, swinging, drinking or floored)
      this.rig.fps = this.input.aimHeld && me.move !== Move.Flight && me.phase === Phase.Alive && !me.paused && me.skyT === 0
        && me.dodgeT === 0 && me.meleeT === 0 && me.blade === 0 && me.drinkT === 0 && me.knockT === 0 && me.offerKind === 0 && !this.saloon?.aboard;
      this.rig.fpsZoom = activeWeaponId(me) === W.BoltSniper ? 16 : 0;
      this.rig.update(dt, this.world, px, py, pz, this.input.yaw, this.input.pitch, me.move, speed, this.input.aimHeld && me.move !== Move.Flight, me.phase === Phase.Down);
      { const c = this.stage.camera, rad = Math.PI / 360; this.input.zoomScale = Math.min(1, Math.tan(c.fov * rad) / Math.tan(this.rig.baseFov * rad)); }
      this.stage.followShadow(this.v1.set(px, py, pz));
    }

    // ---- remote state
    const nPlayers = this.session.players(this.netPlayers);
    this.netPlayerCount = nPlayers;
    this.resolveAim(me, px, py, pz);

    // ---- views (feel first: it resolves this frame's deaths before the robots draw)
    const fxRate = this.feel ? this.feel.update(dt) : 1;
    this.frameDt = dt;
    this.buildEnemyRS(me, px, py, pz, nPlayers, dt);
    this.supportFx();
    const nCow = this.buildCowboyRS(me, px, py, pz, nPlayers);
    this.nCow = nCow;
    this.classFx?.update(this.cowboyRS, nCow, dt, this.time);
    this.waterFx(me, nCow, dt);
    this.robots?.sync(this.enemyRS, this.enemyCount, dt, this.time);
    // the saloon / launch / drop / hotdog scenes bend the cowboys and may take the camera
    const hideHud = this.saloon ? this.saloon.frame(dt, this.time, me, px, py, pz, this.cowboyRS, nCow) : false;
    this.weapons?.rack.begin();
    this.cowboys?.sync(this.cowboyRS, nCow, dt, this.time);
    this.saloon?.afterCowboys(dt);
    if (this.weapons && this.viewmodel && me) {
      const vi = this.vmIn;
      vi.on = this.rig.fps; vi.lens = this.rig.fpsW; vi.id = me.id; vi.cls = me.cls;
      vi.vx = me.vx; vi.vy = me.vy; vi.vz = me.vz; vi.grounded = me.grounded === 1; vi.steady = me.steady / STEADY_TICKS;
      this.viewmodel.frame(dt, this.stage.camera, vi, this.weapons.rack, this.world);
    }
    this.weapons?.rack.end();
    this.weapons?.update(dt, (f) => this.session.bulletDisplayTick(f, this.alpha), this.netEnemies, this.enemyCount);
    this.syncBullets();
    this.fxTime += dt * fxRate; // hit-stop: the FX clock all but stops for a beat on a kill
    this.fx?.update(dt * fxRate, this.fxTime, this.stage.camera);
    this.shockwaves();
    if (me && this.feel) {
      const alive = me.phase === Phase.Alive && !me.paused;
      const cls = [[255, 186, 80], [255, 120, 50], [170, 120, 255], [120, 220, 255]][me.cls & 3] as [number, number, number];
      this.feel.triggers(activeWeaponId(me), alive && me.mag > 0 && me.reloadT === 0, alive && me.move !== Move.Flight, cls, this.session.slot);
    }
    HAZE.uTime.value = this.time;
    if (me) HAZE.uFocus.value.set(px, py + 1.1, pz); else HAZE.uFocus.value.set(0, -1e5, 0);
    const pushers = this.jetWash(nCow, dt);
    // time of day runs on the shared server clock, so the whole posse sees the same sky
    this.worldView?.setDayClock(this.session.simTick / TICK_RATE);
    this.worldView?.update(dt, this.time, this.stage.camera.position, pushers);
    this.runVisuals(dt, me);

    // ---- sound
    // the ears sit between the camera and your cowboy: your own gun is close, the world turns with the camera
    this.stage.camera.getWorldDirection(this.v2);
    const cam = this.stage.camera.position;
    if (me) this.v1.set(cam.x + (px - cam.x) * 0.55, cam.y + (py + 1.4 - cam.y) * 0.55, cam.z + (pz - cam.z) * 0.55);
    else this.v1.copy(cam);
    this.sfx.setListener(this.v1, this.v2);
    const earX = this.v1.x, earY = this.v1.y, earZ = this.v1.z;
    if (me) {
      const jet = me.move === Move.Hover ? 0.5 : me.move === Move.Flight ? 0.8 : 0;
      if (jet && !this.jetOn) this.fx?.dust(this.v1.set(px, py, pz), 0.4);
      this.jetOn = jet > 0;
    }
    {
      const snap = this.session.latest;
      const waveOn = snap?.dirState === DirState.Wave;
      const lowHp = me && me.phase === Phase.Alive && me.hp < 30 ? 0.35 + 0.65 * (1 - me.hp / 30) : 0;
      const mus = this.musicNow(waveOn);
      this.worldAudio.update(dt, this.time, this.enemyRS, this.enemyCount, this.cowboyRS, nCow, this.jetOf,
        earX, earY, earZ, mus, this.musicIntensity(mus, waveOn), lowHp);
    }

    // ---- post + HUD
    this.hurtPulse = Math.max(0, this.hurtPulse - dt * 2.2);
    this.flash = Math.max(0, this.flash - dt * 5);
    this.chroma = Math.max(0, this.chroma - dt * 3);
    const u = this.stage.post.material.uniforms;
    u.uHurt.value = this.hurtPulse + (me && me.hp < 30 && me.phase === Phase.Alive ? 0.25 + 0.15 * Math.sin(this.time * 6) : 0);
    const fs = this.fx?.screen;
    u.uHurt.value = Math.max(u.uHurt.value as number, fs?.hurt ?? 0);
    if (fs && fs.flash > this.flash) (u.uFlashColor.value as THREE.Color).setRGB(fs.flashR, fs.flashG, fs.flashB);
    else (u.uFlashColor.value as THREE.Color).setRGB(1, 0.95, 0.82);
    u.uFlash.value = Math.max(this.flash, fs?.flash ?? 0);
    u.uChroma.value = Math.max(this.chroma, fs?.chroma ?? 0);
    if (fs && fs.shake > 0) { this.rig.shake(fs.shake); fs.shake = 0; }
    const aboard = this.amAboard();
    u.uDesat.value = me ? (me.phase === Phase.Down ? 0.8 : me.paused && !aboard ? 0.6 : me.skyT > 0 && !aboard ? me.skyT * 0.6 : 0) : 0;
    u.uExtract.value = this.extractT;
    if (this.saloon) {
      u.uDesat.value = Math.max(u.uDesat.value as number, this.saloon.postFx.desat);
      u.uFlash.value = Math.max(u.uFlash.value as number, this.saloon.postFx.flash);
    }
    u.uHasHud.value = hideHud ? 0 : 1;
    this.buildHud(me, px, py, pz, nPlayers);
    this.review.frame(dt, this.time, me, this.hudState);
    this.hud.render(this.hudState);
    const info = this.stage.renderer.info;
    info.reset();
    this.lights.update(this.stage.scene);
    this.stage.post.render(this.stage.scene, this.stage.camera, this.time);
    this.draws = info.render.calls; this.tris = info.render.triangles;

    // ---- frame stats
    this.frameMs = this.frameMs * 0.9 + (performance.now() - t0) * 0.1;
    this.fpsAcc += dt; this.fpsN++;
    if (this.time - this.fpsT > 0.5) { this.fps = this.fpsN / this.fpsAcc; this.fpsAcc = 0; this.fpsN = 0; this.fpsT = this.time; }
  }

  // ------------------------------------------------------------------ the run
  /** Pull the boss's bodies out of the robot list: they have their own view, hit volumes and HUD bar.
   *  They end up in netEnemies[enemyCount..] and in bossFrame.units. */
  private splitBoss() {
    const f = this.bossFrame;
    f.n = 0; f.primary = null;
    let n = this.enemyCount;
    for (let i = 0; i < n; i++) {
      const e = this.netEnemies[i];
      if (!isBossType(e.type)) continue;
      n--;
      this.netEnemies[i] = this.netEnemies[n]; this.netEnemies[n] = e;
      f.units[f.n++] = e;
      i--;
    }
    this.enemyCount = n;
    // which boss: the fight's own payload, else the one this island announces (preload its model)
    const run = this.session.latest?.run;
    const kind = run?.boss?.kind || run?.bossKind || BossKind.None;
    if (kind && (!this.bossView || this.bossView.kind !== kind)) {
      let v = this.bossViews.get(kind);
      const make = BOSS_CLIENTS[kind];
      if (!v && make && this.bossCtx) { v = make(this.bossCtx); this.bossViews.set(kind, v); }
      if (v) this.bossView = v;
    }
    // the extraction pursuer (sim/bosses/leviathan.ts) loads during the boss fight, before the bell summons it
    if (run?.bossKind && !this.bossViews.has(PURSUER) && BOSS_CLIENTS[PURSUER] && this.bossCtx) this.bossViews.set(PURSUER, BOSS_CLIENTS[PURSUER]!(this.bossCtx));
    this.bossLive = !!run?.boss && !!this.bossView && run.boss.kind === this.bossView.kind;
    if (this.bossLive) {
      f.net = run!.boss!;
      for (let i = 0; i < f.n; i++) if (f.units[i].id === f.net.eid) f.primary = f.units[i];
    }
  }

  private amAboard() {
    const run = this.session.latest?.run;
    return !!run && ((run.boardMask >> (this.session.pid & 31)) & 1) === 1;
  }

  /** Ray against the boss's part volumes (its view predicts what the server will resolve). */
  private bossRay(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxT: number, ap: number): BossAim {
    const a = this.bossAim;
    a.t = -1;
    if (!this.bossLive || !this.bossView) return a;
    return this.bossView.ray(this.bossFrame, ox, oy, oz, dx, dy, dz, maxT, ap, a);
  }

  private runVisuals(dt: number, me: PlayerState | null) {
    const run = this.session.latest?.run ?? null;
    const ph = run ? run.phase : -1;
    if (ph !== this.lastPhase) this.onPhase(ph, run);
    // the bonus clock's last ten seconds tick out loud
    if (run?.bonus && ph === RunPhase.Waves) {
      const left = Math.ceil(run.clock / TICK_RATE);
      if (left <= 10 && left > 0 && left !== this.bonusTick) { this.bonusTick = left; this.sfx.play('ui_tick', undefined, { pitch: 1.5 - left * 0.04, volume: left <= 3 ? 1 : 0.7 }); }
    } else this.bonusTick = -1;
    // ---- the saloon doors: online once the island is cleared, blown wide by the warp
    const gateOpen = ph === RunPhase.GateOpen || ph === RunPhase.Warping || ph === RunPhase.ExtractTraverse;
    const warp = run && ph === RunPhase.Warping ? clamp(1 - run.timer / (WARP_SECS * TICK_RATE), 0, 1) : 0;
    const toBonus = !!run && ph === RunPhase.Warping && run.warpBonus;
    const next = this.nextIsland(run);
    this.gateCh = gateOpen ? Math.min(1, this.gateCh + dt / 1.6) : 0;
    this.worldView?.setGateState(gateOpen, toBonus ? 0 : warp, next.toUpperCase());
    // the red bonus doors: lit while the bonus is on offer, blown open when that's where the posse goes
    const bonusOpen = !!run && (run.bonusOpen || toBonus);
    this.worldView?.setBonusGateState(bonusOpen, toBonus ? warp : 0);
    if (this.fx && this.world) {
      // one charge emitter: on whichever lit door is nearer the camera
      const g = this.world.gate, bd = this.world.bonusDoor, cam = this.stage.camera.position;
      const useBonus = bonusOpen && !!bd && (toBonus || Math.hypot(cam.x - bd.x, cam.z - bd.z) < Math.hypot(cam.x - g.x, cam.z - g.z));
      if (useBonus && bd) this.fx.gateCharge(bd.x, this.world.bonusDoorY() + 2.6, bd.z, 1.9, 1, 0, this.gateCh * (0.6 + 0.4 * warp));
      else this.fx.gateCharge(g.x, this.world.gateY() + 2.6, g.z, 1.9, 1, 0, this.gateCh * (0.6 + 0.4 * (toBonus ? 0 : warp)));
    }
    if (warp > 0.55) { this.flash = Math.max(this.flash, (warp - 0.55) * 1.6); this.chroma = Math.max(this.chroma, warp * 0.5); this.rig.shake(warp * 0.05); }
    // ---- extraction grey wash (it eases in over ~2 s, like Godot's)
    const extracting = (ph >= RunPhase.ExtractBell && ph <= RunPhase.ExtractBoard) || ph === RunPhase.ExtractTraverse || (ph === RunPhase.Warping && !!run?.warpBack) || (ph === RunPhase.Ended && this.runResult === 0);
    this.extractT += ((extracting ? 1 : 0) - this.extractT) * (1 - Math.exp(-dt * 1.4));
    if (this.extractT < 0.002) this.extractT = 0;
    // ---- power-up drops and the buffs on cowboys
    this.powerupView?.update(run?.pickups ?? [], run?.buffs ?? [], dt, this.cowboyAt, this.stage.camera.position);
    // ---- livestock
    this.critterView?.sync(run?.critters ?? [], dt, this.time, (x, z, y) => this.world.support(x, z, y));
    // ---- the boss
    this.bossFrame.dt = dt; this.bossFrame.time = this.time;
    this.bossView?.update(this.bossLive ? this.bossFrame : null, this.time, dt);
    // the view a new boss replaced (the island boss when the pursuer rises) still finishes its death and hides
    for (const v of this.bossViews.values()) if (v !== this.bossView) v.update(null, this.time, dt);
    // ---- the bell + the train
    if (this.extractView && run) {
      const show = ph >= RunPhase.ExtractBell && ph <= RunPhase.ExtractBoard;
      let train = 0;
      if (ph === RunPhase.ExtractHoldout) train = clamp(1 - run.timer / (HOLDOUT_SECS * TICK_RATE), 0, 1);
      else if (ph === RunPhase.ExtractBoard) train = 1;
      else if (ph === RunPhase.Ended && this.runResult === 0) train = 1 + Math.max(0, this.time - this.endedAt - 1.5) * 0.35;
      this.extractView.update(dt, this.time, show, ph >= RunPhase.ExtractHoldout, train);
    }
    void me;
  }

  /** where the cowboy with this id is drawn (null when hidden or absent) */
  /** the pack (playtest 3, Nick): past 80 % heat it smokes, thicker toward the top; on fire it pours smoke and the flames show */
  private packSmoke(x: number, y: number, z: number, yaw: number, heatF: number, onFire: boolean, dt: number) {
    const fx = this.fx;
    if (!fx || (heatF < 0.8 && !onFire)) return;
    const k = onFire ? 1 : (heatF - 0.8) / 0.2;
    if (Math.random() > dt * (3 + 9 * k)) return;
    // the pack sits on the back, a little over a metre up
    const bx = x + Math.sin(yaw) * 0.3, bz = z + Math.cos(yaw) * 0.3;
    fx.buildingSmoke(bx, y + 1.25, bz, 0.08 + 0.12 * k, onFire ? 0.85 : 0.6);
    if (onFire && Math.random() < 0.5) fx.element(this.v1.set(bx, y + 1.3, bz), El.Fire, 0.35);
  }
  /** this frame's dt, for the render-state builders that have no dt of their own (the pack smoke) */
  private frameDt = 0;
  private cowboyAt = (pid: number) => {
    for (let i = 0; i < this.nCow; i++) { const r = this.cowboyRS[i]; if (r.id === pid) return r.hidden ? null : r; }
    return null;
  };

  /** where the saloon doors lead from here */
  private nextIsland(run: RunSnap | null) {
    if (!run) return '';
    if (run.bonus) return islandName(run.village, ISLANDS_PER_VILLAGE - 1);
    if (run.warpBack || run.phase === RunPhase.ExtractTraverse) return islandName(run.village, Math.max(0, run.island - 1));
    return run.island + 1 < ISLANDS_PER_VILLAGE ? islandName(run.village, run.island + 1) : '';
  }

  /** which score the moment wants: the saloon aboard, then the run's phase (as Godot's MusicPlayer did) */
  private musicNow(waveOn: boolean): MusicState {
    const sal = this.saloon;
    if (sal && sal.stageNow !== RunStage.Run) {
      // the hotdog scene sits in the wipe's silence; the launch keeps the saloon tune under the sendoff cue
      if (sal.stageNow === RunStage.Saloon && sal.reason === SaloonReason.Wipe && sal.hotdogPlaying) return 'defeat';
      return 'saloon';
    }
    const run = this.session.latest?.run;
    if (!run) return 'island';
    switch (run.phase) {
      case RunPhase.BossIntro: case RunPhase.Boss: return 'boss';
      case RunPhase.ExtractBell: case RunPhase.ExtractHoldout: case RunPhase.ExtractBoard: case RunPhase.ExtractTraverse: return 'extract';
      case RunPhase.Warping: return run.warpBack ? 'extract' : 'island';
      case RunPhase.Ended: return this.runResult === 0 ? 'victory' : 'defeat';
      default: void waveOn; return 'island';
    }
  }

  /** how hard the island bed's combat layer pushes */
  private musicIntensity(mus: MusicState, waveOn: boolean) {
    if (mus === 'boss') return 1;
    if (mus === 'extract') return 0.8;
    const run = this.session.latest?.run;
    if (run?.bonus && run.phase === RunPhase.Waves) return 1;
    return waveOn ? Math.min(1, 0.4 + this.enemyCount / 20) : 0.15;
  }

  /** a run phase change: stings and the one-shot moments */
  private onPhase(ph: number, run: RunSnap | null) {
    const prev = this.lastPhase;
    this.lastPhase = ph;
    if (prev < 0 || !run) return;
    switch (ph) {
      case RunPhase.GateOpen:
        if (run.bonus && run.clock > 0) {
          // the prize vault: the clear, then the vault's loot landing
          this.sfx.play('island_cleared'); this.sfx.play('powerup_pickup', undefined, { delay: 0.45, pitch: 0.9 }); this.sfx.play('powerup_pickup', undefined, { delay: 0.7, pitch: 1.2 });
          this.input.rumble(0.4, 0.4, 260);
        } else if (run.bonus) {
          // G.U.L.P. pulled the plug: a klaxon and the power draining out
          this.sfx.play('service_klaxon', undefined, { volume: 0.6 }); this.sfx.play('powerup_denied', undefined, { delay: 0.35, pitch: 0.7 });
        } else { this.sfx.play('island_cleared'); this.input.rumble(0.3, 0.3, 200); }
        if (run.bonusOpen) this.sfx.play('service_klaxon', undefined, { delay: 1.6, volume: 0.35, pitch: 1.15 }); // the red doors light up
        break;
      case RunPhase.Warping:
        if (run.warpBonus) { this.sfx.play('warp', undefined, { pitch: 0.82 }); this.sfx.play('service_klaxon', undefined, { volume: 0.45 }); }
        else this.sfx.play('warp');
        break;
      case RunPhase.Waves: if (run.bonus && prev === RunPhase.Breather) this.sfx.play('boss_phase', undefined, { pitch: 1.15, volume: 0.7 }); break;
      case RunPhase.Breather: if (prev !== RunPhase.Warping) this.sfx.play('objective'); break;
      case RunPhase.BossIntro: this.sfx.play('boss_intro'); break;
      case RunPhase.ExtractBell: this.sfx.play('boss_phase', undefined, { pitch: 0.8 }); this.runResult = -1; break;
      case RunPhase.ExtractHoldout: this.sfx.play('objective'); break;
      case RunPhase.ExtractBoard: this.sfx.play('objective', undefined, { pitch: 1.2 }); break;
      case RunPhase.Ended: this.endedAt = this.time; break;
    }
  }

  // ------------------------------------------------------------------ aim assist (pad only)
  // Console-shooter assist, kept honest: friction (the stick slows over a robot), a gentle rotational
  // pull while you're moving or turning (never when the stick is still: no aimbot), and a short
  // ADS snap when L2 comes on with a robot near the crosshair. Mouse aim is never touched.
  // Light and on the chest from the hip; stronger and on the head down the sights (weapons/aimassist.ts).
  private assistAds = false;
  private snapT = 0;
  private snapYaw = 0;
  private snapPitch = 0;
  private aimAssist(dt: number) {
    const inp = this.input;
    const me = this.session.me;
    if (inp.glyphs !== 'pad' || !me || !this.world || me.move === Move.Flight || me.phase !== Phase.Alive || inp.paused) {
      inp.lookScale = 1; this.snapT = 0; this.assistAds = inp.aimHeld; return;
    }
    const o = this.stage.camera.position;
    const cp = Math.cos(inp.pitch);
    const fx = -Math.sin(inp.yaw) * cp, fy = Math.sin(inp.pitch), fz = -Math.cos(inp.yaw) * cp;
    const ads = inp.aimHeld, rule = ads ? ASSIST.ads : ASSIST.hip;
    const tax = stanceMul(me.cls, me.sprint === 1 && me.move === Move.Ground);
    let bestA = 9, bYaw = 0, bPitch = 0;
    for (let i = 0; i < this.enemyCount; i++) {
      const e = this.netEnemies[i];
      if (e.hp <= 0) continue;
      const d = ENEMIES[e.type];
      const dx = e.x - o.x, dy = assistY(e, ads) - o.y, dz = e.z - o.z;
      const dist = Math.hypot(dx, dy, dz);
      if (dist < 1.5 || dist > (ads ? 90 : 60)) continue;
      const dot = (dx * fx + dy * fy + dz * fz) / dist;
      if (dot < 0.8) continue;
      const ang = Math.acos(Math.min(1, dot));
      const cone = Math.atan2((ads ? d.headR * 2 + d.radius : d.radius) * rule.catch * tax + 0.4, dist);
      const a = ang / cone;
      if (a < bestA) {
        // line of sight to the aim point
        this.world.raycast(o.x, o.y, o.z, dx / dist, dy / dist, dz / dist, dist, this.hit);
        if (this.hit.kind !== 0 && this.hit.t < dist - d.radius) continue;
        bestA = a; bYaw = Math.atan2(-dx, -dz); bPitch = Math.atan2(dy, Math.hypot(dx, dz));
      }
    }
    // ADS snap on the rising edge of L2
    if (ads && !this.assistAds && bestA < 1) { this.snapT = 0.14; this.snapYaw = bYaw; this.snapPitch = bPitch; }
    this.assistAds = ads;
    if (this.snapT > 0) {
      const k = Math.min(1, dt / this.snapT) * 0.75;
      inp.yaw += wrapAngle(this.snapYaw - inp.yaw) * k;
      inp.pitch += (this.snapPitch - inp.pitch) * k;
      this.snapT = Math.max(0, this.snapT - dt);
    }
    if (bestA >= 1) { inp.lookScale += (1 - inp.lookScale) * Math.min(1, dt * 10); return; }
    const near = 1 - bestA;
    const friction = 1 - rule.friction * tax * near * near * (3 - 2 * near);
    inp.lookScale += (friction - inp.lookScale) * Math.min(1, dt * 14);
    // rotational pull only while the player is actively moving or turning
    const activity = Math.max(Math.hypot(inp.mx, inp.my), inp.lookMag);
    if (activity > 0.15) {
      const k = rule.pull * tax * dt * near * activity;
      inp.yaw += wrapAngle(bYaw - inp.yaw) * Math.min(0.2, k);
      inp.pitch += (bPitch - inp.pitch) * Math.min(0.2, k * 0.6);
    }
  }

  // ------------------------------------------------------------------ aim
  private resolveAim(me: PlayerState | null, px: number, py: number, pz: number) {
    const cam = this.stage.camera;
    const dir = cam.getWorldDirection(this.v2);
    const o = cam.position;
    // the shot bends onto a robot inside the assist cone (pad only): its chest from the hip, its head down the sights
    if (me && this.input.glyphs === 'pad' && me.move !== Move.Flight && me.phase === Phase.Alive && !this.input.paused)
      bendShot(o.x, o.y, o.z, dir, this.input.aimHeld, me.cls, stanceMul(me.cls, me.sprint === 1 && me.move === Move.Ground), this.netEnemies, this.enemyCount, this.world, this.hit);
    this.world.raycast(o.x, o.y, o.z, dir.x, dir.y, dir.z, 250, this.hit);
    let best = this.hit.t;
    let target: NetEnemy | null = null;
    for (let i = 0; i < this.enemyCount; i++) {
      const e = this.netEnemies[i];
      const d = ENEMIES[e.type];
      const th = raySphere(o.x, o.y, o.z, dir.x, dir.y, dir.z, e.x, e.y + d.headY, e.z, d.headR * 1.15);
      const tb = raySphere(o.x, o.y, o.z, dir.x, dir.y, dir.z, e.x, e.y + d.bodyY, e.z, d.radius);
      const t = th >= 0 && (tb < 0 || th < tb) ? th : tb;
      if (t >= 0 && t < best) { best = t; target = e; }
    }
    this.aimTarget = target;
    this.aimBossPart = '';
    const bh = this.bossRay(o.x, o.y, o.z, dir.x, dir.y, dir.z, best, me ? weaponOf(me).ap : 0);
    if (bh.t >= 0 && bh.t < best) { best = bh.t; this.aimTarget = null; this.aimBossPart = bh.label; }
    this.aimPt.x = o.x + dir.x * best; this.aimPt.y = o.y + dir.y * best; this.aimPt.z = o.z + dir.z * best; this.aimPt.eid = this.aimTarget ? this.aimTarget.id : 0;
    // aim from the cowboy's eye through what's under the crosshair (parallax-correct)
    const ax = o.x + dir.x * best, ay = o.y + dir.y * best, az = o.z + dir.z * best;
    if (me) {
      const ex = px, ey = py + EYE_HEIGHT, ez = pz;
      const dx = ax - ex, dy = ay - ey, dz = az - ez;
      const flat = Math.hypot(dx, dz);
      // targets closer than the shoulder offset fall back to the camera's own direction, blended
      // (a hard switch made the cowboy and his shots flick as the crosshair crossed near things)
      const w = smoothstep(1.2, 2.6, flat) * smoothstep(2.5, 4.5, best);
      this.aimYaw = this.input.yaw + wrapAngle(Math.atan2(-dx, -dz) - this.input.yaw) * w;
      this.aimPitch = this.input.pitch + (Math.atan2(dy, flat) - this.input.pitch) * w;
      if (me.move === Move.Flight) { this.aimYaw = this.input.yaw; this.aimPitch = this.input.pitch; }
      this.aimDist = Math.hypot(dx, dy, dz);
    }
    this.aimOffYaw = wrapAngle(this.aimYaw - this.input.yaw);
    this.aimOffPitch = this.aimPitch - this.input.pitch;
  }
  private aimOffYaw = 0;
  private aimOffPitch = 0;
  private vmIn = { on: false, lens: 0, id: 0, cls: 0, vx: 0, vy: 0, vz: 0, grounded: true, steady: 0 };
  /** a cowboy's muzzle: yours comes off the first-person gun while it's up */
  private muzzleOf(id: number, out: THREE.Vector3): boolean {
    if (this.viewmodel && id === this.session.me?.id && this.viewmodel.muzzleOf(out)) return true;
    return this.cowboys ? this.cowboys.muzzle(id, out) : false;
  }

  // ------------------------------------------------------------------ support robots
  /** Disruptor heal auras and Engineer repair beams. A repairing engineer is in windup with its aim
   *  point on an ally's body (the gas lob aims at a cowboy), so no extra wire state is needed. */
  private supportFx() {
    const fx = this.fx;
    if (!fx) return;
    for (let i = 0; i < this.enemyCount; i++) {
      const e = this.netEnemies[i];
      if (e.hp <= 0) continue;
      if (e.type === EType.Disruptor) fx.supportAura(e.id, e.x, e.y + 0.05, e.z, 6);
      else if (e.type === EType.Engineer && e.st === 1) {
        for (let k = 0; k < this.enemyCount; k++) {
          const o = this.netEnemies[k];
          if (o === e || o.hp <= 0 || Math.abs(o.x - e.aimX) > 0.6 || Math.abs(o.z - e.aimZ) > 0.6) continue;
          const d = ENEMIES[e.type];
          fx.repairBeam(e.x, e.y + d.bodyY + 0.4, e.z, e.aimX, e.aimY, e.aimZ);
          break;
        }
      }
    }
  }

  // ------------------------------------------------------------------ render states
  private buildEnemyRS(me: PlayerState | null, px: number, py: number, pz: number, nPlayers: number, dt: number) {
    for (let i = 0; i < this.enemyCount; i++) {
      const e = this.netEnemies[i];
      const r = this.enemyRS[i] ?? (this.enemyRS[i] = {} as EnemyRS);
      r.id = e.id; r.type = e.type; r.x = e.x; r.y = e.y; r.z = e.z; r.yaw = e.yaw;
      r.st = e.st; r.prog = e.prog; r.hp = e.hp; r.shield = e.shield; r.flags = e.flags; r.flags2 = e.flags2; r.ext = e.ext; r.parts = e.parts;
      // visible damage (playtest 3): the stumps of shot-off parts smoke; a robot in the red smokes from its body too
      if (this.fx && (e.parts || e.hp < 0.4) && e.hp > 0 && Math.random() < dt * (e.parts ? 6 : 3)) {
        const d = ENEMIES[e.type], rx = Math.cos(e.yaw), rz = -Math.sin(e.yaw);
        const part = e.parts ? [0, 1, 2, 3].filter((k) => (e.parts >> k) & 1)[Math.floor(Math.random() * 4) % Math.max(1, [0, 1, 2, 3].filter((k) => (e.parts >> k) & 1).length)] : 3;
        const ox = part === 1 ? rx * d.radius : part === 2 ? -rx * d.radius : 0, oz = part === 1 ? rz * d.radius : part === 2 ? -rz * d.radius : 0;
        const oy = part === 0 ? d.headY : part === 3 ? d.bodyY * 0.4 : d.bodyY;
        this.fx.buildingSmoke(e.x + ox, e.y + oy, e.z + oz, 0.14, 0.75);
      }
      const hitAt = this.enemyHitAt.get(e.id);
      r.hitAge = hitAt === undefined ? 99 : this.time - hitAt;
      const pp = this.prevEnemyPos.get(e.id);
      let spd = 0;
      if (pp && dt > 0) { spd = pp.spd * 0.8 + (Math.hypot(e.x - pp.x, e.z - pp.z) / dt) * 0.2; pp.x = e.x; pp.z = e.z; pp.spd = spd; }
      else this.prevEnemyPos.set(e.id, { x: e.x, z: e.z, spd: 0 });
      r.speed = spd;
      // eyes follow the nearest cowboy
      let lx = px, ly = py + 1.4, lz = pz, bd = me ? (e.x - px) ** 2 + (e.z - pz) ** 2 : 1e9;
      for (let k = 0; k < nPlayers; k++) {
        const p = this.netPlayers[k];
        const d = (e.x - p.x) ** 2 + (e.z - p.z) ** 2;
        if (d < bd) { bd = d; lx = p.x; ly = p.y + 1.4; lz = p.z; }
      }
      r.lookX = lx; r.lookY = ly; r.lookZ = lz;
      r.aimX = e.aimX; r.aimY = e.aimY; r.aimZ = e.aimZ; r.hasAim = e.st === 1 && (e.aimX !== 0 || e.aimZ !== 0);
      if (this.feel) { this.feel.decorate(r); this.feel.damaged(e); }
    }
    if (this.prevEnemyPos.size > this.enemyCount * 2 + 20) {
      const live = new Set(this.netEnemies.slice(0, this.enemyCount).map((e) => e.id));
      for (const k of this.prevEnemyPos.keys()) if (!live.has(k)) this.prevEnemyPos.delete(k);
    }
  }

  /** weight lane: the last hurting hit and the last knockdown per cowboy, for the pose lane */
  private hurtAt = new Map<number, { at: number; dx: number; dz: number; pow: number; shield: boolean }>();
  private knockAt = new Map<number, { at: number; dx: number; dz: number; kind: number; speed: number }>();
  private warpAt = new Map<number, number>();
  /** the Dune shield skins (fx/shieldskin.ts): breaks, refills and the Charge's pour, from every cowboy's readings */
  private shields = new ShieldTracker();
  /** wildcard cards: the draft overlay while an offer is on the table, and the hand's names for the HUD (pooled) */
  private draft: CardDraft | null = null;
  private handOut: string[] = [];
  /** bullet drop (sim/game.ts dropSegments): the predicted round's arc pieces and the segment ray (pooled) */
  private segs: ShotSeg[] = [];
  private segRay = { ox: 0, oy: 0, oz: 0, dx: 0, dy: 0, dz: 0 };
  private weightRS(r: CowboyRS, id: number) {
    const h = this.hurtAt.get(id);
    if (h) { r.hurtAge = this.time - h.at; r.hurtDX = h.dx; r.hurtDZ = h.dz; r.hurtPow = h.pow; r.hurtShield = h.shield; } else r.hurtAge = 99;
    const k = this.knockAt.get(id);
    if (k) { r.knockAge = this.time - k.at; r.knockKind = k.kind; r.knockDX = k.dx; r.knockDZ = k.dz; r.knockSpeed = k.speed; } else r.knockAge = 99;
    r.warpAge = this.time - (this.warpAt.get(id) ?? -99);
  }

  /** the shield skin's readings for one cowboy, and the shatter when a shield goes (fx/shieldskin.ts) */
  private shieldRS(r: CowboyRS, id: number, cls: number, shield: number, over: number, x: number, y: number, z: number) {
    const max = CLASSES[cls]?.shieldMax ?? 100;
    const alive = r.phase === Phase.Alive && r.skyT <= 0;
    const ev = this.shields.update(r, id, alive ? Math.min(1, shield / max) : 0, alive ? Math.min(1, over / max) : 0, cls === ClassId.Juggernaut, r.warpAge ?? 99, this.time);
    if (ev && this.fx && !r.hidden && alive) {
      const dx = (r.hurtAge ?? 99) < 1 ? -(r.hurtDX ?? 0) : 0, dz = (r.hurtAge ?? 99) < 1 ? -(r.hurtDZ ?? 0) : 0; // toward the shooter
      if (ev & 2) shieldShatter(this.fx, x, y, z, 1.8, true, dx, dz);
      if (ev & 1) shieldShatter(this.fx, x, y, z, 1.8, false, dx, dz);
    }
  }

  private buildCowboyRS(me: PlayerState | null, px: number, py: number, pz: number, nPlayers: number) {
    this.shields.begin();
    let n = 0;
    if (me) {
      const r = this.cowboyRS[n] ?? (this.cowboyRS[n] = {} as CowboyRS);
      r.id = me.id; r.slot = this.session.slot; r.cls = me.cls; r.isLocal = true;
      r.x = px; r.y = py; r.z = pz; r.vx = me.vx; r.vy = me.vy; r.vz = me.vz;
      r.yaw = me.move === Move.Flight ? Math.atan2(-me.vx, -me.vz) : this.aimYaw;
      r.pitch = this.aimPitch;
      r.move = me.move; r.grounded = me.grounded === 1; r.phase = me.phase; r.skyT = me.skyT;
      r.onFire = me.onFire === 1 || me.burnT > 0; r.acid = me.acidT > 0; r.wet = me.wetT > 0; r.cloaked = me.cloakT > 0; r.reloading = me.reloadT > 0;
      this.packSmoke(px, py, pz, r.yaw, me.heat / CLASSES[me.cls].heatMax, me.onFire === 1, this.frameDt);
      r.shotAge = this.time - (this.lastShotAt.get(me.id) ?? -9);
      r.meleeAge = this.time - (this.meleeAt.get(me.id) ?? -9);
      r.hidden = this.rig.hideLocal || this.amAboard(); r.name = this.roster.get(me.id)?.name ?? '';
      r.ads = me.aimOn === 1; r.sprint = me.sprint === 1; r.dodgeK = me.dodgeK === 4 ? 0 : me.dodgeK; // the Phantom's dash (DodgeK.Dash) reads as the ground kind, as it does on the wire r.barrel = me.move === Move.Flight && me.dodgeT > 0; r.barrelSide = r.barrel ? barrelSide(me) : 0;
      r.landK = me.landK; r.stall = me.stallT > 0; r.slide = me.slide === 1;
      r.weapon = activeWeaponId(me); r.pistol = weaponOf(me).pistol; r.throwAge = this.weapons?.throwAge(me.id) ?? 99;
      r.drinkAge = this.time - (this.drinkAt.get(me.id) ?? -99);
      r.ditch = this.ditch.get(me.id) ?? 0;
      r.blade = me.blade === 1; r.bladeStep = me.combo; r.slowed = me.timeT > 0;
      r.heat = me.heat / CLASSES[me.cls].heatMax;
      this.weightRS(r, me.id);
      this.shieldRS(r, me.id, me.cls, me.shield, me.over > 0 ? me.over : 0, px, py, pz);
      n++;
    }
    for (let k = 0; k < nPlayers; k++) {
      const p = this.netPlayers[k];
      const r = this.cowboyRS[n] ?? (this.cowboyRS[n] = {} as CowboyRS);
      r.id = p.id; r.slot = p.slot; r.cls = p.cls; r.isLocal = false;
      r.x = p.x; r.y = p.y + p.skyT * p.skyT * 45; r.z = p.z; r.vx = p.vx; r.vy = p.vy; r.vz = p.vz;
      r.yaw = p.move === Move.Flight ? Math.atan2(-p.vx, -p.vz) : p.yaw; r.pitch = p.pitch;
      r.move = p.move; r.grounded = (p.flags & PFlag.Grounded) !== 0; r.phase = p.phase; r.skyT = p.skyT;
      r.onFire = (p.flags & PFlag.OnFire) !== 0 || ((p.flags2 ?? 0) & 1) !== 0; r.acid = ((p.flags2 ?? 0) & 2) !== 0; r.wet = ((p.flags2 ?? 0) & 4) !== 0; r.cloaked = (p.flags & PFlag.Cloak) !== 0; r.reloading = (p.flags & PFlag.Reload) !== 0;
      this.packSmoke(r.x, r.y, r.z, r.yaw, p.heat / CLASSES[p.cls].heatMax, (p.flags & PFlag.OnFire) !== 0, this.frameDt);
      r.shotAge = this.time - (this.lastShotAt.get(p.id) ?? -9);
      r.meleeAge = (p.flags & PFlag.Melee) ? Math.min(0.1, this.time - (this.meleeAt.get(p.id) ?? -9)) : this.time - (this.meleeAt.get(p.id) ?? -9);
      const bm = this.session.latest?.run?.boardMask ?? 0;
      r.hidden = ((bm >> (p.id & 31)) & 1) === 1; r.name = this.roster.get(p.id)?.name ?? '';
      const mf = p.mflags ?? 0;
      r.ads = (mf & MFlag.Aim) !== 0; r.sprint = (mf & MFlag.Sprint) !== 0; r.barrel = (mf & MFlag.Barrel) !== 0;
      r.stall = (mf & MFlag.Stall) !== 0; r.dodgeK = (mf >> 4) & 3; r.barrelSide = r.barrel ? (r.dodgeK === 3 ? -1 : 1) : 0; if (r.barrel) r.dodgeK = 2; r.landK = (mf >> 6) & 3;
      r.slide = p.move === Move.Dodge && r.dodgeK === 0 && r.sprint;
      r.weapon = WEAPONS[p.weapon] ? p.weapon : -1; r.pistol = weaponDef(p.weapon).pistol; r.throwAge = this.weapons?.throwAge(p.id) ?? 99;
      r.drinkAge = this.time - (this.drinkAt.get(p.id) ?? -99);
      r.ditch = this.ditch.get(p.id) ?? 0;
      r.blade = ((p.blade ?? 0) & 1) === 1; r.bladeStep = this.meleeStep.get(p.id) ?? ((p.blade ?? 0) >> 1) & 3; r.slowed = ((p.blade ?? 0) & 16) !== 0;
      r.heat = p.heat / CLASSES[p.cls].heatMax;
      this.weightRS(r, p.id);
      this.shieldRS(r, p.id, p.cls, p.shield, ((p.flags2 ?? 0) & 8) ? CLASSES[p.cls].shieldMax : 0, r.x, r.y, r.z);
      n++;
    }
    this.shields.end();
    return n;
  }

  /**
   * Water (fx/water.ts): every cowboy's skim spray, jet dimple, hover corona, wading ripples and
   * wake, splashes sized to the impact speed, waterfall bursts and the cooling steam. WaterFx keeps
   * each cowboy's previous height / vy itself, so splashes come from the rendered state (local and
   * remote alike) with no sim or protocol change.
   */
  private waterFx(me: PlayerState | null, n: number, dt: number) {
    const fx = this.fx, w = this.world;
    if (!fx || !w) return;
    const wfx = fx.water;
    wfx.begin();
    const off = me ? 1 : 0;
    for (let i = 0; i < n; i++) {
      const r = this.cowboyRS[i];
      if (r.hidden || r.skyT > 0 || r.phase === Phase.Dead) continue;
      const wy = w.waterAt(r.x, r.z);
      const fall = w.inFall(r.x, r.y + 1, r.z);
      const p = r.isLocal ? me : this.netPlayers[i - off];
      const hf = p ? p.heat / (CLASSES[r.cls]?.heatMax ?? 100) : 0;
      const ev = wfx.cowboy(r.id, r.x, r.y, r.z, r.vx, r.vy, r.vz, r.yaw, r.move, r.grounded,
        wy === NO_GROUND ? -1e9 : wy, fall, this.jetOf(r.id), hf, dt, r.isLocal);
      if (ev) {
        const e = wfx.evPos;
        if (ev & 1) this.sfx.play('el_wet', e, { volume: (r.isLocal ? 0.55 : 0.4) + 0.45 * wfx.lastSplash, pitch: 1.15 - 0.4 * wfx.lastSplash });
        if (ev & 4) this.sfx.play('el_wet', e, { volume: 0.5, pitch: 1.3 });
        if (ev & 2) this.sfx.play('cool_hiss', e, { volume: (r.isLocal ? 0.5 : 0.3) + 0.5 * wfx.lastCool });
      }
    }
    wfx.end();
  }

  private tmpPos = { x: 0, y: 0, z: 0 };
  private syncBullets() {
    if (!this.fx) return;
    let n = 0;
    for (const b of this.session.bullets.values()) {
      if (b.predictedHit) continue;
      const t = this.session.bulletDisplayTick(b, this.alpha);
      bulletPosAt(b, t, this.tmpPos);
      const o = this.bulletList[n] ?? (this.bulletList[n] = { id: 0, kind: 0, x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, age: 0 });
      o.id = b.id; o.kind = b.kind; o.x = this.tmpPos.x; o.y = this.tmpPos.y; o.z = this.tmpPos.z;
      this.feel?.whiz(b.id, o.x, o.y, o.z);
      const g = BULLETS[b.kind].gravity, tt = Math.max(0, (t - b.tick0) / TICK_RATE);
      o.vx = b.vx; o.vy = b.vy - g * tt; o.vz = b.vz; o.age = tt;
      n++;
    }
    this.fx.bullets(this.bulletList, n);
  }

  // ------------------------------------------------------------------ predicted actions
  private onPredicted(p: PlayerState, inp: InputFrame, out: StepOut, tick: number) {
    this.review.onPredicted(p, inp, out);
    const fx = this.fx;
    const pos = this.tmpPos; pos.x = p.x; pos.y = p.y + 1; pos.z = p.z;
    if (out.shots && fx && this.cowboys) {
      this.lastShotAt.set(p.id, this.time);
      const w = weaponOf(p);
      const style = weaponStyle(w.id);
      const tracks = shotTracks(w, out.mod);
      // the Duel Blaster's rounds are bolts (ordnance): the server's Ev.OrdSpawn draws them; here only the muzzle
      const pellets = w.proj === 0 && w.id !== W.DuelBlaster ? w.pellets * (out.mod === ShotMod.Double ? 2 : 1) : 0;
      if (w.id === W.DuelBlaster) { const from = this.v1; if (!this.muzzleOf(p.id, from)) from.set(p.x, p.y + 1.4, p.z); const r = shotRay(this.session.seed, p, tick, 0, inp, out.spreadMul); fx.muzzle(from, this.v3.set(r.dx, r.dy, r.dz), style, true); }
      let hitsHere = 0;
      for (let k = 0; k < pellets; k++) {
        const r0 = shotRay(this.session.seed, p, tick, k, inp, out.spreadMul);
        // tracking rounds bend onto the robot nearest the crosshair, exactly as the server will
        const tracked = tracks && trackRay(r0, w.track, w.range, this.netEnemies, this.enemyCount, this.world, this.hit) >= 0;
        // bullet drop: the same arc the server walks (sim/game.ts dropSegments), so the tracer lands where the hit does
        const nSeg = dropSegments(r0, w.range, tracked ? 0 : w.muzzleV, this.segs);
        const r = this.segRay;
        let best = 0, kind = 0, head = false, shE = -1; // shE: the shielded robot this round stops on
        for (let si = 0; si < nSeg; si++) {
        const sg = this.segs[si];
        r.ox = sg.ox; r.oy = sg.oy; r.oz = sg.oz; r.dx = sg.dx; r.dy = sg.dy; r.dz = sg.dz;
        this.world.raycast(r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, sg.len, this.hit);
        best = this.hit.t; kind = this.hit.kind ? 1 : 0; head = false;
        shE = -1;
        for (let i = 0; i < this.enemyCount; i++) {
          const e = this.netEnemies[i];
          const d = ENEMIES[e.type];
          const th = tracked ? -1 : raySphere(r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, e.x, e.y + d.headY, e.z, d.headR * 1.15);
          if (th >= 0 && th < best) { best = th; kind = 3; head = true; shE = e.shield > 0 ? e.id : -1; }
          const tb = raySphere(r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, e.x, e.y + d.bodyY, e.z, d.radius);
          if (tb >= 0 && tb < best - 0.05) { best = tb; kind = (e.flags & EFlag.Hyper) || ENEMIES[e.type].armor > w.ap ? 4 : e.shield > 0 ? 5 : 2; head = false; shE = e.shield > 0 ? e.id : -1; }
        }
        const bh = this.bossRay(r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, best, w.ap);
        if (bh.t >= 0 && bh.t < best) { best = bh.t; kind = bh.kind; head = bh.head; }
        // friendly fire: your round stops on a teammate in the way, same hitboxes as the server
        for (let i = 0; i < this.netPlayerCount; i++) {
          const o = this.netPlayers[i];
          if (o.id === p.id || o.phase !== Phase.Alive || (o.flags & PFlag.Paused)) continue;
          const th = raySphere(r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, o.x, o.y + PLAYER_HEIGHT - 0.2, o.z, 0.26);
          if (th >= 0 && th < best) { best = th; kind = 2; head = false; }
          const tb = raySphere(r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, o.x, o.y + PLAYER_HEIGHT * 0.45, o.z, PLAYER_RADIUS + 0.05);
          if (tb >= 0 && tb < best - 0.05) { best = tb; kind = 2; head = false; }
        }
        if (best < sg.len - 1e-4 || this.hit.kind !== 0) break;
        }
        const to = this.v2.set(r.ox + r.dx * best, r.oy + r.dy * best, r.oz + r.dz * best);
        const from = this.v1;
        if (!this.muzzleOf(p.id, from)) from.set(p.x, p.y + 1.4, p.z);
        raisedMuzzle(from, p.x, p.y, p.z, r.dx, r.dy, r.dz);
        fx.tracer(from, to, style, true);
        if (k === 0) fx.muzzle(from, this.v3.set(r.dx, r.dy, r.dz), style, true);
        this.v3.set(r.dx, r.dy, r.dz);
        this.feel?.shotFx(w.id, from, to, this.v3, true, k);
        // only the first few pellets get the full flash card; the rest spark (a shotgun read, not a strobe)
        if (kind === 5 || (shE >= 0 && kind === 3)) {
          // stopped on a shield: the round sprays back off the skin and the skin lights from the strike (fx/shieldskin.ts)
          if (k < 3) shieldBlock(fx, to.x, to.y, to.z, -r.dx, -r.dy, -r.dz, false, 1);
          this.robots?.shieldHit(shE, to.x, to.y, to.z);
        } else if (kind && k < 3) fx.impact(to, this.v3.set(this.hit.nx, this.hit.ny, this.hit.nz), kind);
        if (kind) this.feel?.impactFx(w, kind, to, this.v3.set(r.dx, r.dy, r.dz), this.hit.nx, this.hit.ny, this.hit.nz);
        if (kind >= 2) { this.hitAt = this.time; if (head) this.headAt = this.time; hitsHere++; }
        if (k === 0) { this.shotFrom.copy(from); this.shotDir.set(r0.dx, r0.dy, r0.dz); }
      }
      this.sightShot(p, w, out, hitsHere > 0);
      if (w.proj === 0) { const [sn, pitch, vol] = this.feel ? this.feel.shotSound(w.id, weaponSfx(w.id)) : weaponSfx(w.id); this.sfx.play(sn, pos, { volume: 0.9 * vol, pitch }); }
      if (this.feel) {
        if (w.proj !== 0) { if (!this.muzzleOf(p.id, this.shotFrom)) this.shotFrom.set(p.x, p.y + 1.4, p.z); aimDir(inp.yaw, inp.pitch, this.shotDir); raisedMuzzle(this.shotFrom, p.x, p.y, p.z, this.shotDir.x, this.shotDir.y, this.shotDir.z); }
        this.feel.shot(w, this.shotFrom, this.shotDir, p.aimOn === 1);
      }
      else { this.rig.shake(w.kick); if (w.rumble > 0) this.input.rumble(w.rumble * 0.6, w.rumble, 50 + w.rumble * 60); }
    }
    if (out.throw || out.shots) this.weapons?.predicted(p, inp, tick, out.throw, out.shots > 0);
    if (p.cookT > 0 || out.cookOff) this.weapons?.cooking(p, out.cookOff, this.input);
    if (out.swap) this.sfx.play('reload', pos, { volume: 0.35, pitch: 1.6 });
    if (out.shove) { this.rig.shake(0.2); fx?.dust(this.v1.set(p.x, p.y, p.z), 1.2); }
    if (out.melee) { this.meleeAt.set(p.id, this.time); this.sfx.play('melee', pos); }
    if (out.drink) this.startDrink(p.id, p.x, p.y, p.z, true);
    if (out.dodge) this.sfx.play('dodge', pos);
    if (out.special) { this.sfx.play('dodge', pos, { pitch: 0.7 }); this.rig.shake(0.15); this.classFx?.special(p.id, p.cls, true, p.cls === 2 ? out.warpX : p.x, p.y, p.cls === 2 ? out.warpZ : p.z, p.yaw); }
    if (out.jump) this.sfx.play('jump', pos, { volume: 0.5 });
    if (out.launched) { this.sfx.play('jump', pos, { pitch: 0.7 }); fx?.dust(this.v1.set(p.x, p.y, p.z), 1); }
    if (out.land) { this.sfx.play('land', pos, { volume: Math.min(1, out.land / 20) }); fx?.dust(this.v1.set(p.x, p.y, p.z), Math.min(1, out.land / 18)); if (out.land > 14) this.rig.shake(out.land > 15 ? 0.4 : 0.2); if (p.id === this.session.pid) { this.rig.land(out.land); if (out.land > 15) this.input.rumble(0.8, 0.4, 160); } }
    if (out.breach >= 0 || out.breachOut >= 0) this.breachFx(p, out.breach >= 0 ? out.breach : out.breachOut, out.breach < 0, true);
    if (out.bonk > 0) this.bonkFx(p, out);
    else if (out.bump > 0 && !this.bumpHeld && this.time - this.bumpAt > 0.45) { this.bumpAt = this.time; this.bumpFx(p, out.bump); }
    this.bumpHeld = out.bump > 0 || out.bonk > 0; // leaning on a wall is one bump, not one a tick
    if (out.reload) { this.sfx.play(weaponOf(p).rounds ? 'reload_round' : 'reload', pos); this.feel?.reload(activeWeaponId(p), p.x, p.y, p.z, -Math.sin(p.yaw), -Math.cos(p.yaw)); }
    if (out.denied) { this.deniedAt = this.time; this.sfx.play('denied'); }
  }

  // ------------------------------------------------------------------ immersion: the world answers bumps (James, 2026-10-24)
  /** Flew through a house wall (sim/destruct.ts BREACH): the hole goes in now (the server's Ev.Breach confirms it), boards fly. */
  /**
   * What presses the grass (world/grass.ts uPush): each cowboy's body, or, hovering or flying low, his jet's wash
   * from above, which flattens a wide ruffling disc under him and kicks dust off dry ground (immersion pass).
   */
  private washPool: { x: number; y: number; z: number; w: number; dustT: number }[] = [];
  private washOut: { x: number; y: number; z: number; w: number }[] = [];
  private jetWash(n: number, dt: number) {
    const out = this.washOut;
    out.length = 0;
    for (let i = 0; i < n; i++) {
      const c = this.cowboyRS[i];
      const o = this.washPool[i] ?? (this.washPool[i] = { x: 0, y: 0, z: 0, w: 1, dustT: 0 });
      o.x = c.x; o.y = c.y; o.z = c.z; o.w = 1;
      const jet = c.move === Move.Hover || c.move === Move.Flight;
      if (jet && !c.grounded && this.world) {
        const g = this.world.support(c.x, c.z, c.y), alt = c.y - g;
        if (alt > -0.5 && alt < 7) {
          const wash = (1 - Math.max(0, alt) / 7) * (c.move === Move.Hover ? 1 : 0.75);
          o.y = g; o.w = 1 + wash * 2.2;
          // dust boils off dry ground under a low jet (water has its own dimple)
          o.dustT -= dt;
          if (o.dustT <= 0 && wash > 0.35 && !(this.world.waterAt(c.x, c.z) > g - 0.5)) {
            o.dustT = 0.14;
            this.fx?.dust(this.v1.set(c.x + (Math.random() - 0.5) * 1.2, g, c.z + (Math.random() - 0.5) * 1.2), wash * 0.55);
          }
        }
      }
      out.push(o);
    }
    return out;
  }
  private bumpHeld = false;
  private bumpAt = -9;
  private breachFx(p: PlayerState, b: number, exit: boolean, mine: boolean) {
    const w = this.session.world;
    if (!w || !w.dest.buildings[b]) return;
    const sp = Math.hypot(p.vx, p.vz) || 1, dx = p.vx / sp, dz = p.vz / sp;
    const at = w.dest.breachPoint(b, p.x, p.y + 0.9, p.z, dx, dz, exit);
    w.dest.applyBreach(b, at.x, at.y, at.z, at.face | (exit ? 4 : 0));
    this.breachBurst(at.x, at.y, at.z, at.face, dx, dz, exit, mine);
  }
  private breachBurst(x: number, y: number, z: number, face: number, dx: number, dz: number, exit: boolean, mine: boolean) {
    const nx = face === 0 ? 1 : face === 1 ? -1 : 0, nz = face === 2 ? 1 : face === 3 ? -1 : 0;
    this.fx?.wallBreach(x, y, z, nx, nz, dx, dz, exit);
    const at = { x, y, z };
    this.sfx.play('roof_timber', at, { volume: exit ? 0.75 : 1, pitch: exit ? 1.15 : 1 });
    this.sfx.play('robot_debris', at, { volume: 0.8, pitch: 0.8 });
    if (!mine) return;
    // through the wall: a hard jolt and a crack of the lens, the pads thump; out the far side a lighter one
    this.rig.shake(exit ? 0.25 : 0.4);
    this.rig.punch(exit ? -0.02 : -0.05, (Math.random() - 0.5) * 0.06, (Math.random() - 0.5) * 0.1, exit ? 3 : -4, 18);
    this.input.rumble(exit ? 0.6 : 1, exit ? 0.5 : 0.8, exit ? 120 : 200);
  }
  /** Flew into something hard (the game hurts him for it; this is how it looks and feels). Rock spits chips. */
  private bonkFx(p: PlayerState, out: StepOut) {
    const x = p.x + out.bonkX * 0.6, z = p.z + out.bonkZ * 0.6, y = p.y + 1.1;
    this.fx?.rockBonk(x, y, z, -out.bonkX, -out.bonkZ, Math.min(1.5, out.bonk / 14));
    this.sfx.play('robot_knock', { x, y, z }, { volume: 0.9, pitch: 0.6 });
    // the lens snaps back from the hit and rolls off it
    this.rig.punch(0.07, 0, (Math.random() < 0.5 ? -1 : 1) * 0.12, -6, 14);
  }
  /** A softer knock: walked, sprinted or hovered into a wall, a robot or a rock. Felt, not hurt. */
  private bumpFx(p: PlayerState, speed: number) {
    const k = Math.min(1, (speed - 3) / 6);
    this.rig.punch(0.012 + 0.025 * k, (Math.random() - 0.5) * 0.02, (Math.random() - 0.5) * 0.05 * k, -1 - 2 * k, 22);
    this.sfx.play('land', { x: p.x, y: p.y + 1, z: p.z }, { volume: 0.25 + 0.35 * k, pitch: 1.25 });
    this.input.rumble(0.25 + 0.4 * k, 0.15 + 0.3 * k, 70 + 60 * k);
    if (k > 0.4) this.fx?.dust(this.v1.set(p.x + Math.sin(-p.yaw) * 0.4, p.y + 0.6, p.z - Math.cos(p.yaw) * 0.4), 0.3 * k);
  }

  // ------------------------------------------------------------------ feel helpers
  private enemyById(id: number): NetEnemy | null {
    for (let i = 0; i < this.enemyCount; i++) if (this.netEnemies[i].id === id) return this.netEnemies[i];
    return null;
  }
  private playerPos(id: number, out: THREE.Vector3): THREE.Vector3 | null {
    const me = this.session.me;
    if (me && id === me.id) return out.set(me.x, me.y + 1.4, me.z);
    for (let i = 0; i < this.netPlayers.length; i++) { const p = this.netPlayers[i]; if (p && p.id === id) return out.set(p.x, p.y + 1.4, p.z); }
    return null;
  }
  /** explosion shockwaves: a ring of refraction in the post pass, projected each frame */
  private shockwaves() {
    const u = this.stage.post.material.uniforms.uShock as { value: THREE.Vector4[] } | undefined;
    if (!u) return;
    const list = this.feel?.shocks ?? [];
    const cam = this.stage.camera;
    for (let i = 0; i < 4; i++) {
      const sh = list[i], o = u.value[i];
      if (!sh || this.feel === null) { o.set(0, 0, 0, 0); continue; }
      const age = this.feel.now - sh.at;
      const v = this.v3.set(sh.x, sh.y, sh.z);
      const dist = v.distanceTo(cam.position);
      v.project(cam);
      if (v.z > 1 || age < 0 || age > 0.6) { o.set(0, 0, 0, 0); continue; }
      // ring radius in screen height units: the world ring (fast, then slowing) over distance
      const worldR = sh.r * 2.2 * (1 - Math.exp(-age * 7));
      const scr = worldR / Math.max(1, dist) / Math.tan((cam.fov * Math.PI) / 360);
      o.set(v.x * 0.5 + 0.5, v.y * 0.5 + 0.5, scr * 0.5, sh.k * (1 - age / 0.6) ** 2);
    }
  }

  // ------------------------------------------------------------------ server events
  private enemyPos(id: number, out: THREE.Vector3) {
    for (let i = 0; i < this.enemyCount; i++) {
      const e = this.netEnemies[i];
      if (e.id === id) return out.set(e.x, e.y + ENEMIES[e.type].bodyY, e.z);
    }
    return null;
  }

  private onEvent(e: GameEvent, _tick: number) {
    const f = e.f;
    const fx = this.fx;
    const myId = this.session.pid;
    this.feel?.onEvent(e, (id) => this.enemyById(id));
    this.progress?.onEvent(e, myId);
    this.review.onEvent(e);
    this.pickupCard.onEvent(e, myId, this.time, this.session.me);
    if (this.weapons?.onEvent(e, myId, this.session.simTick, (id, out) => this.enemyPos(id, out))) return;
    switch (e.t) {
      case Ev.Shot: {
        const pid = f[0];
        // your own rounds were drawn when you fired; the overload pulse is the server's, so draw it now
        if (pid === myId && !(f[11] & ShotFlag.Overload)) break;
        if (!fx || !this.cowboys) break;
        if ((f[11] & ShotFlag.Overload) && pid === myId) this.sight.overloadAt = this.time;
        this.lastShotAt.set(pid, this.time);
        const to = this.v2.set(f[1] + f[4] * f[7], f[2] + f[5] * f[7], f[3] + f[6] * f[7]);
        const from = this.v1;
        if (!this.muzzleOf(pid, from)) from.set(f[1], f[2], f[3]);
        raisedMuzzle(from, f[1], f[2] - EYE_HEIGHT, f[3], f[4], f[5], f[6]);
        const style = weaponStyle(f[10]);
        fx.tracer(from, to, style, false);
        // one muzzle per trigger pull (a shotgun's pellets arrive as separate shots)
        const first = this.time - (this.muzzleAt.get(pid) ?? -9) > 0.03;
        if (first) { this.muzzleAt.set(pid, this.time); fx.muzzle(from, this.v3.set(f[4], f[5], f[6]), style, false); }
        this.feel?.shotFx(f[10], from, to, this.v3.set(f[4], f[5], f[6]), false, first ? 0 : 1);
        if (f[8]) fx.impact(to, this.v3.set(-f[4], -f[5], -f[6]), f[8]);
        const [sn, pitch, vol] = weaponSfx(f[10]);
        this.sfx.play(sn, { x: from.x, y: from.y, z: from.z }, { volume: 0.6 * vol, pitch });
        break;
      }
      case Ev.EnemyHit: {
        const [eid, dmg, flags, by, x, y, z] = f;
        this.enemyHitAt.set(eid, this.time);
        const mine = by === myId;
        fx?.damageNumber(this.v1.set(x, y, z), dmg, flags, mine);
        if (flags & 2) this.robots?.shieldHit(eid, x, y, z); // the skin lights from the strike (fx/shieldskin.ts)
        if (fx && dmg > 0 && !(flags & 2)) fx.buildingChips(x, y, z, Math.random() - 0.5, Math.random() - 0.5); // a hull hit throws chips and sparks (playtest 3)
        if (mine && !this.feel) { // the feel lane plays the hit confirms (client/feel/director.ts myHit)
          if (flags & 8) { /* detonation numbers ride the detonate event */ }
          else if (flags & 4) this.sfx.play('armor', { x, y, z }, { volume: 0.7 });
          else if (flags & 2) this.sfx.play('shield_hit', { x, y, z }, { volume: 0.7 });
          else if (flags & 1) this.sfx.play('headshot', undefined, { volume: 0.7 });
          else this.sfx.play('hit', undefined, { volume: 0.5 });
        }
        break;
      }
      case Ev.EnemyDie: {
        const [, type, x, y, z, killer] = f;
        if (isBossType(type)) {
          this.bossView?.bodyDied(type, x, y, z, this.time);
          // a boss made of many bodies (the cube's units) gives kill feedback per body
          if (killer === myId && type !== EType.Cerberus) this.killAt = this.time;
          break;
        }
        fx?.robotDeath(this.v1.set(x, y, z), type);
        this.sfx.play(this.feel?.dieSound(type) ?? (type === EType.Swarmer ? 'toaster_pop' : 'robot_die'), { x, y, z });
        if (killer === myId) { this.killAt = this.time; if (!this.feel) this.sfx.play('kill', undefined, { volume: 0.6 }); }
        this.enemyHitAt.delete(f[0]);
        break;
      }
      case Ev.Prime: {
        const p = this.enemyPos(f[0], this.v1);
        if (p) { fx?.primeBurst(p); this.sfx.play('prime', p); }
        break;
      }
      case Ev.Detonate: {
        const [, pid, cls, x, y, z, radius, chain] = f;
        this.v1.set(x, y, z);
        fx?.detonation(this.v1, radius, cls, chain);
        this.sfx.play('detonate', { x, y, z }, { pitch: 1 + Math.min(0.5, chain * 0.06) });
        const d = this.stage.camera.position.distanceTo(this.v1);
        const near = Math.max(0, 1 - d / 40);
        this.rig.shake(pid === myId ? 0.45 : 0.3 * near);
        this.flash = Math.max(this.flash, pid === myId ? 0.9 : 0.5 * near);
        this.chroma = Math.max(this.chroma, pid === myId ? 1 : 0.4 * near);
        if (pid === myId) this.detAt = this.time;
        if (pid === myId || near > 0.5) this.input.rumble(pid === myId ? 0.8 : 0.5 * near, 0.6, 160);
        break;
      }
      case Ev.PlayerHurt: {
        const [pid, dmg, fxp, fzp, flags] = f;
        // the body flinches away from where it came from (weight lane; cowboys/index.ts reads hurtAge)
        {
          const q = pid === myId ? this.session.me : this.netPlayers.find((o) => o.id === pid);
          if (q && dmg > 0) {
            let dx = q.x - fxp, dz = q.z - fzp; const l = Math.hypot(dx, dz);
            if (l > 1e-3) { dx /= l; dz /= l; } else { dx = -Math.sin(q.yaw); dz = -Math.cos(q.yaw); }
            const h = this.hurtAt.get(pid) ?? { at: 0, dx: 0, dz: 0, pow: 0, shield: false };
            h.at = this.time; h.dx = dx; h.dz = dz; h.pow = Math.min(1.2, dmg / 40);
            h.shield = (flags & 1) !== 0 || q.shield > 0 || (pid === myId ? (this.session.me?.over ?? 0) > 0 : (((q as { flags2?: number }).flags2 ?? 0) & 8) !== 0); // the shield took it (or broke on it): the shell pulses from the impact
            this.hurtAt.set(pid, h);
            // a blocked shot (fx/shieldskin.ts): it stops on the skin facing where it came from and sprays back
            if (h.shield && !(flags & 16) && this.fx) {
              const gold = pid === myId ? (this.session.me?.over ?? 0) > 0 : (((q as { flags2?: number }).flags2 ?? 0) & 8) !== 0;
              const yy = (pid === myId ? q.y : q.y + (q.skyT ?? 0) * (q.skyT ?? 0) * 45) + 1.12 + (Math.random() - 0.5) * 0.12; // the skin's strike point (cowboys/index.ts, 0.62 of him)
              shieldBlock(this.fx, q.x - dx * 0.32, yy, q.z - dz * 0.32, -dx, 0.1, -dz, gold, Math.min(1.4, 0.8 + dmg / 40));
            }
          }
        }
        if (pid === myId && this.session.me) {
          const me = this.session.me;
          this.hurtDirs.push({ angle: 0, at: this.time, wx: fxp, wz: fzp });
          if (this.hurtDirs.length > 6) this.hurtDirs.shift();
          this.hurtPulse = Math.min(1, this.hurtPulse + dmg / 30);
          this.rig.shake(Math.min(0.5, dmg / 40));
          // playtest 1 (Nick): damage has to be felt in the hands: the heavy motor hard, longer with the hit
          this.input.rumble(Math.min(1, 0.5 + dmg / 25), Math.min(1, 0.3 + dmg / 40), 140 + Math.min(220, dmg * 4));
          // the shield took it: a lighter ping, so a hull hit (the one that matters) stays distinct
          if (flags & 16) { this.sfx.play('hurt', undefined, { volume: 1, pitch: 0.82 }); this.sfx.play('land', undefined, { volume: 1, pitch: 0.7 }); } // flew into something: the grunt and the thud
          else if (me.shield > 0 && !(flags & 1)) this.sfx.play('shield_hit', undefined, { volume: Math.min(1, 0.5 + dmg / 40) });
          else this.sfx.play('hurt', undefined, { volume: Math.min(1, 0.4 + dmg / 30) });
          if (flags & 1) { this.shieldBrokeAt = this.time; this.sfx.play('shield_break'); }
          fx?.playerHurt(this.v1.set(me.x, me.y + 1.1, me.z));
        } else {
          const p = this.netPlayers.find((q) => q.id === pid);
          if (p) fx?.playerHurt(this.v1.set(p.x, p.y + 1.1, p.z));
        }
        break;
      }
      case Ev.BulletSpawn: {
        const [id, kind, , x, y, z] = f;
        const pos = { x, y, z };
        if (kind === BKind.Slug) { /* sniper_fire: client/review plays it */ }
        else if (BULLETS[kind].explode > 0) { // bombs, boulders, support grenades and gas canisters
          this.sfx.play('bomb_throw', pos);
          const b = this.session.bullets.get(id);
          if (b) {
            // find where it lands so the posse can see the danger zone
            const land = { x: 0, y: 0, z: 0 };
            let tt = b.tick0;
            for (let i = 0; i < 400; i++) {
              tt += 1;
              bulletPosAt(b, tt, land);
              const g = this.world.support(land.x, land.z, land.y);
              if (land.y <= g + 0.2 || land.y < -60) break;
            }
            const secs = Math.max(0.1, (tt - this.session.simTick) / TICK_RATE + 0.2);
            this.bombMarks.set(id, { x: land.x, y: land.y, z: land.z, until: this.time + secs });
            fx?.bombMarker(id, land.x, land.y, land.z, BULLETS[kind].explode, secs);
          }
        } else this.sfx.play('enemy_shoot', pos, { volume: 0.55 });
        break;
      }
      case Ev.BulletEnd: {
        const [id, x, y, z, hitPid] = f;
        if (this.bombMarks.has(id)) { this.bombMarks.delete(id); fx?.clearBombMarker(id); }
        if (hitPid === 255) fx?.impact(this.v1.set(x, y, z), this.v2.set(0, 1, 0), 1);
        break;
      }
      case Ev.Explosion: {
        const [x, y, z, r, kind] = f;
        this.v1.set(x, y, z);
        fx?.explosion(this.v1, r, kind);
        if (!this.feel || r < 6) this.sfx.play('explosion', { x, y, z }); // bigger ones get the feel lane's explosion_m / _l
        const d = this.stage.camera.position.distanceTo(this.v1);
        this.rig.shake(Math.max(0, 0.5 - d / 50));
        if (d < 18) this.input.rumble(0.6 * (1 - d / 18), 0.4, 140);
        break;
      }
      case Ev.Drink: {
        this.ditch.set(f[0], f[2]);
        if (f[0] !== myId) { const p = this.netPlayers.find((q) => q.id === f[0]); this.startDrink(f[0], p?.x ?? 0, p?.y ?? 0, p?.z ?? 0, false); }
        break;
      }
      case Ev.Melee: {
        if (f[0] !== myId) {
          this.meleeAt.set(f[0], this.time);
          this.meleeStep.set(f[0], f[3] ?? 0);
          const p = this.netPlayers.find((q) => q.id === f[0]);
          if (p) this.sfx.play('melee', { x: p.x, y: p.y + 1, z: p.z });
        }
        if (f[0] === myId && f[1] > 0) { this.hitAt = this.time; this.rig.shake(0.12); }
        break;
      }
      case Ev.SpawnWarn: {
        const [x, y, z, type, ticks] = f;
        fx?.spawnWarn(this.v1.set(x, y, z), type, ticks / TICK_RATE);
        this.spawnMarks.push({ x, y, z, until: this.time + ticks / TICK_RATE + 0.5 });
        if (this.spawnMarks.length > 12) this.spawnMarks.shift();
        this.sfx.play('spawn_warn', { x, y, z }, { volume: 0.6 });
        break;
      }
      case Ev.Down: this.notice(`${this.nameOf(f[0])} IS DOWN`); this.sfx.play('down'); break;
      case Ev.Ricochet: {
        // a round glanced off armour and bounced (ARMOUR.md): sparks where it hit, a dimmer tracer along the bounce,
        // an impact wherever it ended up; a whine past your head if it came back your way
        const [pid, ox, oy, oz, dx, dy, dz, dist, kind] = f;
        if (!fx) break;
        const from = this.v1.set(ox, oy, oz), to = this.v2.set(ox + dx * dist, oy + dy * dist, oz + dz * dist);
        fx.impact(from, this.v3.set(dx, dy, dz), 4);
        fx.tracer(from, to, weaponStyle(f[10]), false);
        if (kind === 1 || kind === 2 || kind === 5) fx.impact(to, this.v3.set(-dx, -dy, -dz), kind === 5 ? 2 : kind);
        this.sfx.play('armor', { x: ox, y: oy, z: oz }, { volume: 0.55, pitch: 1.3 });
        const me = this.session.me;
        if (me && pid !== -1) { const d = Math.hypot(me.x - to.x, me.y + 1.5 - to.y, me.z - to.z); if (d < 3) this.sfx.play('bullet_whiz', { x: to.x, y: to.y, z: to.z }, { volume: 0.7 }); }
        break;
      }
      case Ev.Special: { this.warpAt.set(f[0], this.time); this.classFx?.special(f[0], -1, f[0] === myId); break; } // every class move; the Phantom's pose lane reads it as the warp
      case Ev.Blink: {
        // the Phantom's blink strike (CLASSES.md): he's behind it now; his own camera turns to face back at it
        const [pid, eid, fx0, fz0, tx, ty, tz] = f;
        this.warpAt.set(pid, this.time);
        const ep = this.enemyPos(eid, this.v2);
        if (pid === myId) {
          if (ep) { this.input.yaw = Math.atan2(-(ep.x - tx), -(ep.z - tz)); }
          this.rig.shake(0.35); this.input.rumble(0.5, 0.3, 120);
        }
        this.classFx?.blink(fx0, ty, fz0, tx, ty, tz, pid === myId);
        this.sfx.play('shield_break', this.v1.set(tx, ty + 1, tz), { volume: 0.5 });
        break;
      }
      case Ev.Pound: {
        // the Valkyrie's ground pound landed: a slam the size of the shield she burnt
        const [pid, x, y, z, r] = f;
        fx?.explosion(this.v1.set(x, y + 0.3, z), r, 0);
        fx?.dust?.(this.v1.set(x, y + 0.2, z), 1);
        this.sfx.play('land_hard', this.v1.set(x, y, z), { volume: 1 });
        if (pid === myId) { this.rig.shake(0.8); this.input.rumble(1, 0.7, 260); }
        else { const me = this.session.me; if (me) this.rig.shake(0.5 * Math.max(0, 1 - Math.hypot(me.x - x, me.z - z) / 30)); }
        break;
      }
      case Ev.Card: {
        const [pid, what] = f;
        if (what === 2) { this.sfx.play('island_cleared', undefined, { volume: 0.35 }); if (pid === myId) this.rig.shake(0.15); }
        else if (what === 1 && pid === myId) this.sfx.play('shield_break', undefined, { volume: 0.4 });
        break;
      }
      case Ev.Knock: {
        // knocked off his feet (weight lane): the pose lane plays the fall, the lie and the get-up off knockAge
        const [pid, kx, kz, kind] = f;
        const l = Math.hypot(kx, kz) || 1;
        const k = this.knockAt.get(pid) ?? { at: 0, dx: 0, dz: 0, kind: 1, speed: 0 };
        k.at = this.time; k.dx = kx / l; k.dz = kz / l; k.kind = kind; k.speed = l;
        this.knockAt.set(pid, k);
        const q = pid === myId ? this.session.me : this.netPlayers.find((o) => o.id === pid);
        if (q) { this.sfx.play('land_hard', this.v1.set(q.x, q.y + 0.5, q.z), { volume: kind === 2 ? 1 : 0.7 }); fx?.dust?.(this.v1.set(q.x, q.y + 0.2, q.z), kind === 2 ? 1 : 0.6); }
        if (pid === myId) { this.rig.shake(kind === 2 ? 1 : 0.7); this.input.rumble(1, 0.8, kind === 2 ? 320 : 220); }
        break;
      }
      case Ev.Revive: this.notice(`${this.nameOf(f[1])} REVIVED ${this.nameOf(f[0])}`); this.sfx.play('revive'); break;
      case Ev.Respawn: this.notice(`${this.nameOf(f[0])} REDEPLOYED. ONE LIFE SPENT`); break;
      case Ev.Wave: {
        const [wave, state] = f;
        if (state === DirState.Wave) { this.notice(wave >= 6 ? 'WORK ORDER: METAL BRUTE' : `WORK ORDER ${wave}: CLEAR THE ARENA`); this.sfx.play('wave_start'); }
        else if (state === DirState.Intermission && wave > 0) { this.notice(`ORDER ${wave} FILLED`); this.sfx.play('wave_clear'); }
        else if (state === DirState.Cleared) this.sfx.play('wave_clear', undefined, { pitch: 0.8 });
        break;
      }
      case Ev.Notice: this.notice(e.s ?? ''); this.sfx.play('notice', undefined, { volume: 0.5 }); break;
      case Ev.Roster: {
        try {
          const list = JSON.parse(e.s ?? '[]') as RosterEntry[];
          this.roster.clear();
          for (const r of list) this.roster.set(r.id, r);
          this.onRoster();
        } catch { /* ignore */ }
        break;
      }
      case Ev.Unprime: break;
      case Ev.Island: this.runResult = -1; break;
      case Ev.PowerUp: {
        // [pid, type, el + 1, what, pickup id]
        const [pid, type, el1, what] = f;
        const mine = pid === myId;
        const at = this.cowboyAt(pid);
        if (what === PUEv.Picked) this.sfx.play('powerup_pickup', mine ? undefined : at ?? undefined);
        else if (what === PUEv.Used) {
          this.sfx.play('powerup_use', mine ? undefined : at ?? undefined);
          if (mine) { this.input.rumble(0.35, 0.5, 160); this.notice(`${puLabel(type, el1 - 1)} ON`); }
          if (at) fx?.explosion(this.v1.set(at.x, at.y + 1, at.z), 1.2, 1);
        } else if (what === PUEv.Expired && mine) this.notice(`${puLabel(type, el1 - 1)} WORE OFF`);
        else if (what === PUEv.Denied && mine) { this.sfx.play('powerup_denied'); this.puDeniedAt = this.time; this.notice('ONE BUFF AT A TIME: LET THIS ONE RUN OUT'); }
        break;
      }
      case Ev.Bell: {
        this.extractView?.ring();
        const bp = this.extractView?.bellPos();
        if (bp) { this.sfx.play('bell', bp); fx?.explosion(this.v1.set(bp.x, bp.y, bp.z), 1.6, 1); }
        if (f[0] === myId) { this.hitAt = this.time; this.input.rumble(0.5, 0.3, 120); }
        break;
      }
      case Ev.Critter: {
        // [id, killed, by]: they moo when hit, and a little louder when it's the last one
        const c = this.session.latest?.run?.critters.find((q) => q.id === f[0]);
        if (c) this.sfx.play('moo', { x: c.x, y: c.y + 1.2, z: c.z }, { pitch: c.type === 0 ? 1 : 0.78, volume: f[1] ? 1 : 0.8 });
        break;
      }
      case Ev.Boss:
        this.bossView?.onEvent(f[1], f[2], f[3], f[0], this.bossLive ? this.bossFrame : null, this.time);
        break;
      case Ev.RunEnd: {
        this.runResult = f[0];
        if (f[0] === 0) this.sfx.play('extract_sting');
        else this.sfx.play('down', undefined, { pitch: 0.7 });
        break;
      }
      case Ev.Stage: this.runStage = f[0] as RunStage; this.saloon?.onStage(f[0] as RunStage, f[1] as SaloonReason, f[2]); this.onContract?.(); break;
      case Ev.Contract: this.contract = f[0]; this.onContract?.(); break;
      case Ev.ReadyState: this.saloon?.onReady(f[0], f[1]); break;
      case Ev.Seat: this.saloon?.onSeat(f[0], f[1]); break;
      // a lingering cloud (engineer gas): id, element, x y z, radius, ticks
      case Ev.Hazard: fx?.hazard(f[0], f[1], f[2], f[3], f[4], f[5], f[6] / TICK_RATE); break;
      case Ev.Breach: { // someone else flew through a house (your own was predicted): f = island, building, x y z, face, pid
        if (f[6] === this.session.pid || f[6] === 255) break;
        const q = this.netPlayers.find((o) => o.id === f[6]);
        const sp = q ? Math.hypot(q.vx, q.vz) || 1 : 1;
        this.breachBurst(f[2], f[3], f[4], f[5] & 3, q ? q.vx / sp : 0, q ? q.vz / sp : 0, (f[5] & 4) !== 0, false);
        break;
      }
      case Ev.PartPop: { // a part shot clean off a living robot (playtest 3): a burst of chips, a puff, the armour clang
        if (fx) { for (let i = 0; i < 4; i++) fx.buildingChips(f[2], f[3], f[4], Math.random() - 0.5, Math.random() - 0.5); fx.buildingSmoke(f[2], f[3], f[4], 0.3, 0.8); }
        this.sfx.play('armor', { x: f[2], y: f[3], z: f[4] }, { volume: 0.9, pitch: 0.8 });
        break;
      }
      case Ev.Stagger: { const p = this.enemyPos(f[0], this.v1); if (p) { fx?.explosion(p, 3, 4); this.sfx.play('shield_break', p); } break; }
    }
  }

  private nameOf(id: number) { return (this.roster.get(id)?.name ?? 'SOMEBODY').toUpperCase(); }
  /** a cowboy uncorked a bottle (yours predicted, everyone else's from the server): the chug plays off this */
  private startDrink(id: number, x: number, y: number, z: number, mine: boolean) {
    const at = this.drinkAt.get(id) ?? -99;
    if (this.time - at < 1.2) return; // a replayed prediction or the server echo of the same drink
    this.drinkAt.set(id, this.time);
    const pos = { x, y: y + 1.5, z };
    // the beats of the 1.65 s chug (client/render/cowboys: grab 0.1 s, the swallows 0.43..1.32 s); the heal lands at 1 s
    this.sfx.play('whiskey_uncork', pos, { delay: 0.12 });
    for (let i = 0; i < 3; i++) this.sfx.play('whiskey_gulp', pos, { delay: 0.5 + i * 0.3, pitch: 1 - i * 0.04, volume: mine ? 1 : 0.7 });
    if (mine) { this.sfx.play('stim', undefined, { delay: 1, volume: 0.7 }); this.input.rumble(0.2, 0.1, 120); }
  }
  /** you reached for the bottle and the belt was empty */
  private dryAt = -9;
  private drinkHeld = false;

  private notice(text: string) {
    this.notices.push({ text, at: this.time });
    if (this.notices.length > 5) this.notices.shift();
  }

  // ------------------------------------------------------------------ HUD
  private blankHud(): HudState { return blankHudState(); }

  private project(x: number, y: number, z: number, out: { sx: number; sy: number; onScreen: boolean; angle: number; dist: number }) {
    const cam = this.stage.camera;
    const v = this.v3.set(x, y, z);
    out.dist = v.distanceTo(cam.position);
    v.project(cam);
    const w = this.hudState.w, h = this.hudState.h;
    const behind = v.z > 1;
    out.sx = (v.x * 0.5 + 0.5) * w;
    out.sy = (-v.y * 0.5 + 0.5) * h;
    out.onScreen = !behind && v.x > -0.95 && v.x < 0.95 && v.y > -0.92 && v.y < 0.92;
    let ax = v.x, ay = -v.y;
    if (behind) { ax = -ax; ay = -ay; }
    out.angle = Math.atan2(ay, ax);
  }

  private objOut = { title: '', detail: '', timer: -1, tone: 0 };
  /** the one line that always says what to do next */
  private objective(run: RunSnap | null, aboard: boolean) {
    const o = this.objOut;
    if (!run) return null;
    const secs = run.timer / TICK_RATE;
    o.timer = -1; o.tone = 0; o.detail = '';
    const isl = islandName(run.village, run.island, run.bonus).toUpperCase();
    const clock = run.clock / TICK_RATE;
    switch (run.phase) {
      case RunPhase.Idle: return null;
      case RunPhase.Breather:
        if (run.bonus) { o.title = isl; o.detail = `BONUS ROUND · ${BONUS_WAVES} ELITE WAVES · BEAT THE ${BONUS_CLOCK_SECS} S CLOCK`; o.tone = 2; }
        else if (this.world?.kind === IslandKind.Boss) { o.title = 'SOMETHING BIG IS COMING'; o.detail = 'FIND COVER: PILLARS, ROCKS, BUILDINGS'; }
        else { o.title = isl; o.detail = run.island === 0 && run.village === 0 ? 'GET YOUR BEARINGS · ROBOTS INBOUND' : 'ROBOTS INBOUND'; }
        o.timer = secs; break;
      case RunPhase.Waves:
        if (run.bonus) {
          // the gauntlet: the clock is the order
          o.title = `BONUS ORDER ${run.wave} OF ${run.waves}`; o.detail = this.enemyCount > 0 ? `BEAT THE CLOCK · ${this.enemyCount} LEFT` : 'BEAT THE CLOCK';
          o.timer = clock; o.tone = clock < 30 ? 2 : 1;
        } else if (run.timer > 0 && run.wave > 0) { o.title = `ORDER ${run.wave} FILLED`; o.detail = 'NEXT ORDER'; o.timer = secs; }
        else { o.title = `WORK ORDER ${run.wave} OF ${run.waves}`; o.detail = this.enemyCount > 0 ? `CLEAR THE ARENA · ${this.enemyCount} LEFT` : 'CLEAR THE ARENA'; }
        break;
      case RunPhase.GateOpen:
        o.title = 'ISLAND CLEAR'; o.tone = 1;
        o.detail = run.bonusOpen ? 'SALOON DOORS ONWARD · OR FLY UP TO THE RED DOORS: BONUS ROUND' : 'WALK THROUGH THE SALOON DOORS';
        break;
      case RunPhase.Warping:
        o.title = 'WARPING'; o.detail = `TO ${(run.warpBack ? islandName(run.village, Math.max(0, run.island - 1)) : run.warpBonus ? islandName(run.village, run.island, true) : this.nextIsland(run)).toUpperCase()}`; o.timer = secs; o.tone = run.warpBonus ? 2 : 1; break;
      case RunPhase.BossIntro:
        o.title = this.bossView?.title ?? 'SOMETHING BIG'; o.detail = this.bossView?.sub ?? ''; o.tone = 2; break;
      case RunPhase.Boss:
        if (this.bossView) this.bossView.objective(this.bossLive ? this.bossFrame : null, o);
        else o.title = 'PUT DOWN THE BOSS';
        break;
      case RunPhase.ExtractTraverse:
        if (run.swing) { o.title = 'THE ISLAND IS GOING'; o.detail = 'GET TO THE SALOON DOORS'; o.timer = secs; o.tone = 2; }
        else { o.title = 'RIDE BACK TO THE BELL'; o.detail = `THROUGH THE SALOON DOORS · ${islandName(run.village, 0).toUpperCase()}`; o.tone = 1; }
        break;
      case RunPhase.ExtractBell:
        o.title = 'RING THE BELL'; o.detail = 'FLY TO THE PLATFORM AND SHOOT IT'; o.tone = 1; break;
      case RunPhase.ExtractHoldout:
        o.title = 'HOLD THE PLATFORM'; o.detail = 'THE TRAIN IS COMING'; o.timer = secs; o.tone = 1; break;
      case RunPhase.ExtractBoard:
        if (aboard) { o.title = 'ABOARD'; o.detail = 'WAITING ON THE POSSE'; }
        else { o.title = 'BOARD THE TRAIN'; o.detail = 'JUMP FOR THE OPEN DOOR'; o.tone = 1; }
        if (run.timer > 0) { o.timer = secs; o.detail = aboard ? 'LAST CALL' : 'LAST CALL · JUMP FOR THE DOOR'; o.tone = 2; }
        break;
      case RunPhase.Ended:
        if (this.runResult === 0) { o.title = 'EXTRACTED'; o.detail = 'G.U.L.P. THANKS YOU FOR YOUR SERVICE'; }
        else return null;
        break;
      default: return null;
    }
    return o;
  }

  private puOut = { name: '', color: '', name2: '', active: false, activeName: '', activeColor: '', frac: 0, denied: false };
  private powerupHud(me: PlayerState | null) {
    if (!me) return undefined;
    const o = this.puOut;
    const css = (hex: number) => `#${hex.toString(16).padStart(6, '0')}`;
    o.name = me.pu ? puLabel(me.pu, me.puEl - 1) : '';
    o.color = me.pu ? css(puColor(me.pu, me.puEl - 1)) : '';
    o.name2 = me.pu2 ? puLabel(me.pu2, me.pu2El - 1) : '';
    o.active = me.puAct !== PU.None;
    o.activeName = o.active ? puLabel(me.puAct, me.puActEl - 1) : '';
    o.activeColor = o.active ? css(puColor(me.puAct, me.puActEl - 1)) : '';
    const dur = o.active ? (PU_SECS[me.puAct] ?? 1) * TICK_RATE : 1;
    o.frac = o.active ? clamp(me.puT / dur, 0, 1) : 0;
    o.denied = this.time - this.puDeniedAt < 0.8;
    return o.name || o.active ? o : undefined;
  }

  private bossOut = newHudBoss();
  private bossHud(_run: RunSnap | null) {
    return this.bossLive && this.bossView ? this.bossView.hud(this.bossFrame, this.aimBossPart, this.bossOut) : null;
  }

  /** the per-gun sight's running state (HUD) */
  private sight = { overloadAt: -9, burstHits: 0, burstRound: 0, burstAt: -9, t: 0 };
  private sightOut: HudSight = { gun: 0, ads: false, spread: 0, track: 0, lock: false, lockX: 0, lockY: 0, spin: 0, steady: 0, bloom: 0, burstHits: 0, burstRound: 0, overloadAge: 9, charge: -1, chambers: 0, chamberMax: 0 };
  private sightRay = { ox: 0, oy: 0, oz: 0, dx: 0, dy: 0, dz: 0 };
  private sightProj = { sx: 0, sy: 0, onScreen: false, angle: 0, dist: 0 };
  /** a predicted shot, for the sights that count (Pulse Rifle burst segments) */
  private sightShot(p: PlayerState, w: WeaponDef, out: StepOut, hit: boolean) {
    void out;
    if (w.perk !== Perk.Overload) return;
    const sg = this.sight;
    const round = w.burst - 1 - p.burstN;
    if (round === 0) sg.burstHits = 0;
    sg.burstRound = round + 1; sg.burstAt = this.time;
    if (hit) sg.burstHits++;
  }

  private buildSight(s: HudState, me: PlayerState, px: number, py: number, pz: number) {
    const w = weaponOf(me), so = this.sightOut, cam = this.stage.camera;
    if (w.proj !== Proj.Hitscan) { s.sight = undefined; return; }
    const ads = me.aimOn === 1;
    const moving = Math.abs(me.vx) + Math.abs(me.vz) > 1 || !me.grounded ? 1.6 : 1;
    let mul = 1;
    if (w.perk === Perk.SpinUp) mul = 0.6 + 0.8 * me.spin / SPIN_TICKS;
    else if (w.perk === Perk.Fan) mul = 1 + me.bloom * FAN_SPREAD;
    const deg = (ads ? w.adsSpreadDeg : w.spreadDeg) * moving * mul;
    const half = Math.tan(cam.fov * Math.PI / 360);
    // the ring eases to the new spread (~60 ms) so aiming in reads as the sight tightening, not a jump
    const target = Math.tan(deg * Math.PI / 180) / half;
    const dt = Math.min(0.1, Math.max(0, this.time - this.sight.t)); this.sight.t = this.time;
    so.spread = so.gun === w.id ? so.spread + (target - so.spread) * (1 - Math.exp(-dt * 16)) : target;
    so.gun = w.id; so.ads = ads;
    so.track = w.track > 0 ? Math.tan(w.track * Math.PI / 180) / half : 0;
    so.spin = me.spin / SPIN_TICKS; so.steady = me.steady / STEADY_TICKS; so.bloom = me.bloom;
    const sg = this.sight;
    if (this.time - sg.burstAt > 0.45) { sg.burstHits = 0; sg.burstRound = 0; }
    so.burstHits = sg.burstHits; so.burstRound = sg.burstRound; so.overloadAge = this.time - sg.overloadAt;
    so.charge = w.fire === 3 && me.chargeT > 0 ? Math.min(1, me.chargeT / w.charge) : -1;
    so.chambers = me.mag; so.chamberMax = w.mag;
    // tracking: which robot would a round bend to right now (the full-charge plasma previews while charging)
    so.lock = false;
    if (w.track > 0 && (w.perk !== Perk.PlasmaLock || so.charge >= 0)) {
      const r = this.sightRay;
      r.ox = px; r.oy = py + EYE_HEIGHT; r.oz = pz;
      let dx = this.aimPt.x - r.ox, dy = this.aimPt.y - r.oy, dz = this.aimPt.z - r.oz;
      const l = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
      r.dx = dx / l; r.dy = dy / l; r.dz = dz / l;
      const i = trackRay(r, w.track, w.range, this.netEnemies, this.enemyCount, this.world, this.hit);
      if (i >= 0) {
        const e = this.netEnemies[i];
        this.project(e.x, e.y + ENEMIES[e.type].bodyY, e.z, this.sightProj);
        if (this.sightProj.onScreen) { so.lock = true; so.lockX = this.sightProj.sx; so.lockY = this.sightProj.sy; }
      }
    }
    s.sight = so;
  }

  private cue: HudClassCue = { detReady: false, detVerb: '', detHint: '', primeReady: true, primeHint: '', extender: '', cloakSecs: 0 };
  /** Will my class verbs work on the robot under the reticle right now? The same tests sim/game.ts runs
   *  (shotHitsEnemy, resolveMelee), read off the interpolated robot, so the HUD can say "now" or say what to do. */
  private classCue(me: PlayerState): HudClassCue {
    const q = this.cue, t = this.aimTarget, air = me.grounded !== 1, cloaked = me.cloakT > 0;
    q.detReady = false; q.detVerb = ''; q.detHint = ''; q.primeReady = true; q.primeHint = '';
    q.cloakSecs = me.cloakT / TICK_RATE;
    q.extender = me.chainT > 0 ? EXTENDER[me.cls] : '';
    if (!t) return q;
    // facing: the robot's forward against the line from me to it (> 0 = it has its back to me)
    const dx = t.x - me.x, dz = t.z - me.z, dl = Math.hypot(dx, dz) || 1;
    const back = (-Math.sin(t.yaw) * dx - Math.cos(t.yaw) * dz) / dl;
    switch (me.cls) {
      case ClassId.Gunslinger: q.detReady = true; break; // a headshot always does it; the plate already says so
      case ClassId.Valkyrie:
        q.detReady = air; q.detVerb = 'SHOOT · YOU\'RE AIRBORNE'; q.detHint = 'GET OFF THE GROUND, THEN SHOOT'; break;
      case ClassId.Phantom: {
        const reeling = (t.flags & EFlag.Stun) !== 0;
        q.detReady = cloaked || back > 0.35 || reeling;
        q.detVerb = cloaked ? 'SHOOT · FROM THE SMOKE' : back > 0.35 ? 'SHOOT · ITS BACK IS TURNED' : 'SHOOT · IT\'S REELING';
        q.detHint = 'HOLD R3 TO BLINK BEHIND IT';
        q.primeReady = cloaked || back > 0.2 || this.aimDist < 4.5; q.primeHint = 'HOLD R3 TO BLINK BEHIND IT';
        break;
      }
      case ClassId.Juggernaut:
        // the charge needs shield and its cooldown; the hammer (hold R3) detonates any time, so name it when the charge can't
        q.detReady = true;
        q.detVerb = me.specialCd === 0 && me.shield > 0 ? 'CHARGE IT · OR HOLD R3' : 'HOLD R3 · HAMMER IT';
        q.detHint = 'HOLD R3 · HAMMER IT'; break;
    }
    return q;
  }

  private buildHud(me: PlayerState | null, px: number, py: number, pz: number, nPlayers: number) {
    const s = this.hudState;
    const c = this.stage.renderer.domElement;
    s.w = c.clientWidth || window.innerWidth; s.h = c.clientHeight || window.innerHeight; s.dpr = this.stage.renderer.getPixelRatio();
    s.time = this.time; s.glyphs = this.input.glyphs; s.posseOpen = this.showPosse;
    const snap = this.session.latest;
    if (me) {
      const cd = CLASSES[me.cls];
      s.cls = me.cls; s.className = cd.name.toUpperCase();
      s.slot = this.session.slot; s.name = this.roster.get(me.id)?.name ?? '';
      s.hp = me.hp; s.hpMax = HULL_MAX; s.shield = me.shield; s.shieldMax = cd.shieldMax; s.heat = me.heat; s.heatMax = cd.heatMax;
      s.onFire = me.onFire === 1; s.burning = me.burnT > 0; s.acid = me.acidT > 0; s.wet = me.wetT > 0; s.heatDeniedAge = this.time - this.deniedAt; s.shieldBrokeAge = this.time - this.shieldBrokeAt;
      s.mag = me.mag;
      this.weapons?.hud(s, me);
      this.buildSight(s, me, px, py, pz);
      s.move = me.move; s.grounded = me.grounded === 1; s.phase = me.phase; s.bleedOut = me.phaseT / TICK_RATE; s.reviveFrac = me.reviveT / 120;
      s.paused = me.paused === 1; s.skyT = me.skyT;
      s.chainFrac = me.chainT / cd.chainTicks; s.chainN = me.chainN; s.cloaked = me.cloakT > 0;
      s.primerVerb = cd.primer; s.detonatorVerb = cd.detonator; s.abilityName = cd.ability;
      s.abilityFrac = cd.abilityCd > 0 ? 1 - me.specialCd / cd.abilityCd : 1;
      // the Valkyrie's dash is charges: the dial shows how many are up and the next one filling
      if (me.cls === ClassId.Valkyrie) { s.abilityFrac = (me.dashN + (me.dashN < VDASH_CHARGES ? me.dashT / VDASH_RECHARGE : 0)) / VDASH_CHARGES; s.abilityName = `FIRE DASH · ${me.dashN}`; }
      // the Charge is paid in shield: say so instead of sitting there lit and doing nothing
      if (me.cls === ClassId.Juggernaut && me.shield <= 0) s.abilityName = 'CHARGE · NO SHIELD';
      s.classCue = this.classCue(me);
    }
    const t = this.aimTarget;
    s.aimType = t ? t.type : -1;
    s.aimPrimed = !!t && (t.flags & EFlag.Primed) !== 0;
    s.aimHyper = !!t && (t.flags & EFlag.Hyper) !== 0;
    s.aimHp = t ? t.hp : 0; s.aimShield = t ? t.shield : 0; s.aimDist = this.aimDist;
    this.feel?.hud(s);
    s.hitAge = this.time - this.hitAt; s.killAge = this.time - this.killAt; s.headAge = this.time - this.headAt; s.detonateAge = this.time - this.detAt;
    // hurt arcs relative to where the camera faces
    s.hurtDirs.length = 0;
    for (const hd of this.hurtDirs) {
      const age = this.time - hd.at;
      if (age > 1.6) continue;
      const world = Math.atan2(-(hd.wx - px), -(hd.wz - pz));
      s.hurtDirs.push({ angle: wrapAngle(this.input.yaw - world), age }); // HUD: +pi/2 = from the right
    }
    // posse
    s.posse.length = 0;
    for (let k = 0; k < nPlayers; k++) {
      const p = this.netPlayers[k];
      const ro = this.roster.get(p.id);
      const cd = CLASSES[p.cls];
      const tm: HudTeammate = {
        id: p.id, name: ro?.name ?? '?', slot: p.slot, cls: p.cls, hp: p.hp, hpMax: HULL_MAX, shield: p.shield, shieldMax: cd.shieldMax,
        heat: p.heat, heatMax: cd.heatMax, phase: p.phase, paused: (p.flags & PFlag.Paused) !== 0, connected: ro?.on ?? true,
        reviveFrac: p.reviveFrac, sx: 0, sy: 0, onScreen: false, angle: 0, dist: 0,
      };
      this.project(p.x, p.y + p.skyT * p.skyT * 45 + 2.1, p.z, tm);
      s.posse.push(tm);
    }
    // markers
    s.markers.length = 0;
    this.spawnMarks = this.spawnMarks.filter((m) => m.until > this.time);
    for (const m of this.spawnMarks) { const mk = { kind: 'spawn', t: m.until - this.time } as HudMarker; this.project(m.x, m.y + 1, m.z, mk); s.markers.push(mk); }
    for (const [, m] of this.bombMarks) { const mk = { kind: 'bomb', t: Math.max(0, m.until - this.time) } as HudMarker; this.project(m.x, m.y + 0.2, m.z, mk); s.markers.push(mk); }
    for (const tm of s.posse) if (tm.phase === Phase.Down) { const mk = { kind: 'downed', t: tm.reviveFrac } as HudMarker; mk.sx = tm.sx; mk.sy = tm.sy; mk.onScreen = tm.onScreen; mk.angle = tm.angle; mk.dist = tm.dist; s.markers.push(mk); }
    const run = snap?.run ?? null;
    const ph = run ? run.phase : -1;
    const aboard = this.amAboard();
    if ((ph === RunPhase.GateOpen || ph === RunPhase.Warping) && this.world) {
      const mk = { kind: 'gate', t: this.gateCh } as HudMarker;
      mk.label = this.nextIsland(run).toUpperCase() || 'GATE';
      this.project(this.world.gate.x, this.world.gateY() + 6.4, this.world.gate.z, mk); s.markers.push(mk);
    }
    if (ph === RunPhase.ExtractBell && this.extractView) { const bp = this.extractView.bellPos(); const mk = { kind: 'bell', t: 0 } as HudMarker; this.project(bp.x, bp.y + 2.4, bp.z, mk); s.markers.push(mk); }
    if (ph === RunPhase.ExtractBoard && this.extractView && !aboard) { const dp = this.extractView.doorPos(); const mk = { kind: 'train', t: 0 } as HudMarker; this.project(dp.x, dp.y + 2, dp.z, mk); s.markers.push(mk); }
    if (run?.bonusOpen && this.world?.bonusDoor) {
      const bd = this.world.bonusDoor;
      const mk = { kind: 'bonus', t: this.gateCh, label: 'BONUS ROUND' } as HudMarker;
      this.project(bd.x, this.world.bonusDoorY() + 6.4, bd.z, mk); s.markers.push(mk);
    }
    for (const k of run?.pickups ?? []) {
      if (me && Math.hypot(k.x - me.x, k.z - me.z) < CARD_RANGE) continue;
      const mk = { kind: 'powerup', t: 0, label: puLabel(k.type, k.el) } as HudMarker;
      this.project(k.x, k.y + 2.2, k.z, mk); s.markers.push(mk);
    }
    for (const k of this.weapons?.ord.crateList() ?? []) {
      if (me && Math.hypot(k.x - me.x, k.z - me.z) < CARD_RANGE) continue; // in reach, the pickup card names it
      const mk = { kind: 'crate', t: 0, label: weaponDef(k.wid).short } as HudMarker;
      this.project(k.x, k.y + 2.6, k.z, mk); s.markers.push(mk);
    }
    s.powerup = this.powerupHud(me);
    s.pickup = this.pickupCard.update(this.time, me, this.weapons?.ord.crateList() ?? [], run?.pickups ?? [], PICKUP_BIND[s.glyphs]);
    if (me) { this.handOut.length = 0; for (const f of HAND_FIELDS) { const id = me[f]; if (id) this.handOut.push(CARDS[id].name); } s.cards = this.handOut; }
    s.whiskey = me && me.phase === Phase.Alive ? { n: me.bottles, max: WHISKEY_MAX, heal: me.stimT / HEAL_TICKS, chug: me.drinkT / CHUG_TICKS, low: me.hp < HULL_MAX * 0.45, empty: this.time - this.dryAt < 0.8 } : undefined;
    s.objective = this.objective(run, aboard);
    s.boss = this.bossHud(run);
    s.extract = this.extractT;
    // run
    s.lives = snap?.lives ?? 0; s.wave = snap?.wave ?? 0; s.waveState = snap?.dirState ?? 0; s.tier = snap?.tier ?? 0;
    s.dirTimer = (snap?.dirTimer ?? 0) / TICK_RATE; s.enemiesLeft = this.enemyCount; s.frozen = snap?.frozen ?? false;
    s.notices.length = 0;
    for (const n of this.notices) { const age = this.time - n.at; if (age < 6) s.notices.push({ text: n.text, age }); }
    s.showNet = this.showNet;
    const st = this.session.stats;
    s.net.ping = st.ping; s.net.jitter = st.jitter; s.net.loss = st.loss / 100; s.net.cushion = st.cushion; s.net.corrections = st.corrections;
    s.net.interp = st.interp; s.net.kbpsIn = st.kbpsIn; s.net.kbpsOut = st.kbpsOut; s.net.fps = this.fps; s.net.frameMs = this.frameMs;
    s.net.draws = this.draws; s.net.tris = this.tris; s.net.tickRate = st.tickRate;
    void ENEMY_NAMES; void clamp; void aimDir;
  }
}

/** an empty HUD state (also used by the feel lab) */
export function blankHudState(): HudState {
  return {
    w: 1, h: 1, dpr: 1, time: 0, glyphs: 'kbm', cls: 0, className: '', weaponName: '', slot: 0, name: '',
    hp: 100, hpMax: 100, shield: 0, shieldMax: 1, heat: 0, heatMax: 100, onFire: false, heatDeniedAge: 99, shieldBrokeAge: 99,
    mag: 0, magMax: 1, reloadFrac: -1, move: 0, grounded: true, phase: 0, bleedOut: 0, reviveFrac: 0, paused: false, skyT: 0,
    chainFrac: 0, chainN: 0, cloaked: false, primerVerb: '', detonatorVerb: '', abilityName: '', abilityFrac: 1,
    aimType: -1, aimPrimed: false, aimHyper: false, aimHp: 0, aimShield: 0, aimDist: 0,
    hitAge: 99, killAge: 99, headAge: 99, detonateAge: 99, hurtDirs: [], posse: [], markers: [],
    lives: 0, wave: 0, waveState: 0, tier: 0, dirTimer: 0, enemiesLeft: 0, notices: [], frozen: false, showNet: false,
    net: { ping: 0, jitter: 0, loss: 0, cushion: 0, corrections: 0, interp: 0, kbpsIn: 0, kbpsOut: 0, fps: 0, frameMs: 0, draws: 0, tris: 0, tickRate: 60 },
  };
}
